import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { JarvisGlobe, JarvisGlobeRef } from '@/components/og/JarvisGlobe';
import { VoiceVisualizer } from '@/components/og/VoiceVisualizer';
import { fetchJarvisWeather } from '@/services/jarvisWeatherService';
import { generateSpeechWithTimestamps } from '@/services/unrealSpeechService';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CloudRain, ShieldAlert, Clock, Cpu, Minimize2, Maximize2, Locate, X, Compass, CornerDownLeft, Navigation, Home, Info, Loader2, Plane, Users, Globe2, Zap, Newspaper, MapPin, Languages, Coins, Timer, ExternalLink, Sparkles, CloudSun, Sun, Wifi, Crosshair, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, RefreshCw, Search, SlidersHorizontal, Calendar, Database, Plus, Minus, Copy, Check, Play, Pause, Volume2, MoreVertical, Settings, ArrowLeft
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

const GROQ_API_KEYS = [
  "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",
  "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",
  "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"
];
let currentGroqKeyIndex = 0;
const getGroqApiKey = () => GROQ_API_KEYS[currentGroqKeyIndex];
const rotateGroqApiKey = () => {
  currentGroqKeyIndex = (currentGroqKeyIndex + 1) % GROQ_API_KEYS.length;
};

type FilterMode = 'crises' | 'weather' | 'population' | 'flights' | 'network' | 'gdp' | 'heatmap';
type WindowType = 'chat' | 'info' | 'settings';

interface OSWindow {
  id: string;
  type: WindowType;
  title: string;
  x: number;
  y: number;
  maximized: boolean;
  data?: any;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  images?: string[];
  justGenerated?: boolean;
}

interface ChatTab {
  id: string;
  title: string;
  messages: ChatMessage[];
  isResponding: boolean;
  statusText: string;
}




// Reusable Window Frame Component (Re-themed to #1c1c1c with blue highlights)
const OSWindowFrame = ({
  win,
  onUpdate,
  onClose,
  onFocus,
  zIndex,
  headerExtra,
  children
}: {
  win: OSWindow;
  onUpdate: (id: string, updates: Partial<OSWindow>) => void;
  onClose: (id: string) => void;
  onFocus: (id: string) => void;
  zIndex: number;
  headerExtra?: React.ReactNode;
  children: React.ReactNode;
}) => {
  const winRef = useRef<HTMLDivElement>(null);
  const positionRef = useRef({ x: win.x, y: win.y });
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ pointerX: 0, pointerY: 0, winX: 0, winY: 0 });

  // Sync window position when parent changes it (and we are not dragging)
  useEffect(() => {
    if (!isDraggingRef.current) {
      positionRef.current = { x: win.x, y: win.y };
      if (winRef.current && !win.maximized) {
        winRef.current.style.left = `${win.x}px`;
        winRef.current.style.top = `${win.y}px`;
      }
    }
  }, [win.x, win.y, win.maximized]);

  // Keep DOM styles updated after any component render (e.g. from parent re-renders)
  useLayoutEffect(() => {
    if (winRef.current && !win.maximized) {
      winRef.current.style.left = `${positionRef.current.x}px`;
      winRef.current.style.top = `${positionRef.current.y}px`;
    }
  });

  // Pointer drag event handlers
  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Only drag with left click
    if (e.button !== 0 || win.maximized) return;

    // Don't drag if clicking buttons, inputs, close/maximize actions
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('input') || target.closest('textarea') || target.closest('a')) {
      return;
    }

    isDraggingRef.current = true;
    dragStartRef.current = {
      pointerX: e.clientX,
      pointerY: e.clientY,
      winX: positionRef.current.x,
      winY: positionRef.current.y,
    };

    if (winRef.current) {
      winRef.current.classList.add('dragging-active');
    }

    // Set pointer capture to the header element to keep tracking even if mouse leaves window
    const headerElement = e.currentTarget;
    headerElement.setPointerCapture(e.pointerId);

    onFocus(win.id);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return;

    const dx = e.clientX - dragStartRef.current.pointerX;
    const dy = e.clientY - dragStartRef.current.pointerY;

    // Calculate new position
    const nextX = dragStartRef.current.winX + dx;
    const nextY = dragStartRef.current.winY + dy;

    positionRef.current = { x: nextX, y: nextY };

    if (winRef.current) {
      winRef.current.style.left = `${nextX}px`;
      winRef.current.style.top = `${nextY}px`;
    }
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return;

    isDraggingRef.current = false;

    if (winRef.current) {
      winRef.current.classList.remove('dragging-active');
    }

    const headerElement = e.currentTarget;
    try {
      headerElement.releasePointerCapture(e.pointerId);
    } catch (err) { }

    // Save final position back to parent state
    onUpdate(win.id, { x: positionRef.current.x, y: positionRef.current.y });
  };

  return (
    <motion.div
      ref={winRef}
      onPointerDown={() => onFocus(win.id)}
      initial={{ scale: 0.96, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.96, opacity: 0 }}
      transition={{ duration: 0.15 }}
      style={{
        zIndex,
        position: win.maximized ? 'fixed' : 'absolute',
        left: win.maximized ? 0 : undefined,
        top: win.maximized ? 0 : undefined,
        width: win.maximized ? '100vw' : 'min(92vw, 500px)',
        height: win.maximized ? '100dvh' : 'min(82dvh, 460px)',
        willChange: "transform, width, height, left, top"
      }}
      className={cn(
        "os-window-frame pointer-events-auto border border-[#2d2d2d] bg-[#1c1c1c] flex flex-col shadow-2xl overflow-hidden",
        win.maximized ? "rounded-none" : "rounded-xl"
      )}
    >
      {/* Draggable Header */}
      <div
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        style={{ touchAction: 'none' }}
        className={cn(
          "flex justify-between items-end bg-[#141414] border-b border-[#2d2d2d] select-none",
          (win.type === 'chat' || win.type === 'info') ? "px-2 pt-1.5 pb-0" : "px-4 py-2.5",
          !win.maximized && "cursor-move"
        )}
      >
        <div className="flex items-end text-[10px] text-zinc-300 font-mono tracking-widest uppercase pointer-events-auto h-full">
          {(win.type === 'chat' || win.type === 'info') ? (
            headerExtra
          ) : (
            <div className="flex items-center gap-2">
              <Database className="w-3.5 h-3.5 text-blue-500" />
              {win.title}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1.5 pointer-events-auto pb-1">
          {win.type !== 'chat' && win.type !== 'info' && headerExtra}
          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => onUpdate(win.id, { maximized: !win.maximized })}
            className="p-1 hover:bg-zinc-800 text-zinc-400 hover:text-zinc-200 rounded transition-colors"
          >
            {win.maximized ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
          </button>
          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => onClose(win.id)}
            className="p-1 hover:bg-red-950/35 text-zinc-400 hover:text-red-400 rounded transition-colors"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 flex flex-col relative overflow-hidden bg-[#1c1c1c]">
        {children}
      </div>
    </motion.div>
  );
};

// Isolated clock component
const SystemClock = () => {
  const [currentTime, setCurrentTime] = useState<string>('');

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    };
    updateTime();
    const timer = setInterval(updateTime, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <span className="flex items-center gap-1.5 text-blue-500 font-mono font-medium border-l border-blue-500 pl-3">
      <Clock className="w-3 h-3 text-blue-500 animate-pulse" /> {currentTime}
    </span>
  );
};

// Custom rehype plugin to wrap each word in a span with a data-word-index attribute
const rehypeWordWrap = () => {
  return (tree: any) => {
    let wordIndex = 0;

    const walk = (node: any, parent?: any, index?: number) => {
      if (node.type === 'text') {
        // Skip if parent is code or pre
        if (parent && (parent.tagName === 'code' || parent.tagName === 'pre')) {
          return;
        }

        const text = node.value;
        // Match word characters (alphanumeric and accented characters)
        const regex = /([a-zA-Z0-9\u00C0-\u00FF\u0100-\u017F]+)/g;
        const tokens = text.split(regex);

        const newChildren = tokens.map((token: string) => {
          if (!token) return null;
          const isWord = /^[a-zA-Z0-9\u00C0-\u00FF\u0100-\u017F]+$/.test(token);
          if (isWord) {
            const currentIdx = wordIndex;
            wordIndex++;
            return {
              type: 'element',
              tagName: 'span',
              properties: {
                className: ['speech-word'],
                'data-word-index': String(currentIdx),
              },
              children: [{ type: 'text', value: token }]
            };
          } else {
            return { type: 'text', value: token };
          }
        }).filter(Boolean);

        if (parent && typeof index === 'number') {
          parent.children.splice(index, 1, ...newChildren);
          return newChildren.length;
        }
      }

      if (node.children && Array.isArray(node.children)) {
        let i = 0;
        while (i < node.children.length) {
          const added = walk(node.children[i], node, i);
          if (typeof added === 'number') {
            i += added;
          } else {
            i++;
          }
        }
      }
      return null;
    };

    walk(tree);
  };
};

// Component for single Jarvis Chat Message to handle immediate TTS streaming and word highlighting
const JarvisChatMessage = ({
  msg,
  isAIResponding,
  markdownComponents,
  setLightboxImage,
  isLatest,
  textSize = 'md'
}: {
  msg: ChatMessage;
  isAIResponding: boolean;
  markdownComponents: Components;
  setLightboxImage: (url: string | null) => void;
  isLatest: boolean;
  textSize?: 'sm' | 'md' | 'lg';
}) => {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [timestampsUrl, setTimestampsUrl] = useState<string | null>(null);
  const [timestamps, setTimestamps] = useState<any[]>([]);
  const [highlightedWordIndex, setHighlightedWordIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoadingSpeech, setIsLoadingSpeech] = useState(false);
  const [copied, setCopied] = useState(false);
  const [failedImages, setFailedImages] = useState<Record<string, boolean>>({});
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const visibleImages = useMemo(() => (msg.images || []).filter(imgUrl => !failedImages[imgUrl]), [msg.images, failedImages]);

  const timestampsRef = useRef(timestamps);
  useEffect(() => {
    timestampsRef.current = timestamps;
  }, [timestamps]);

  useEffect(() => {
    if (!containerRef.current) return;

    const previousActive = containerRef.current.querySelectorAll('.speech-word-active');
    previousActive.forEach(el => {
      el.classList.remove('speech-word-active');
    });

    if (isPlaying && highlightedWordIndex !== -1) {
      const activeEl = containerRef.current.querySelector(`[data-word-index="${highlightedWordIndex}"]`);
      if (activeEl) {
        activeEl.classList.add('speech-word-active');
      }
    }
  }, [highlightedWordIndex, isPlaying]);

  const fetchSpeech = async (autoPlay = false) => {
    if (isLoadingSpeech || audioUrl) return;
    setIsLoadingSpeech(true);
    try {
      const result = await generateSpeechWithTimestamps({
        text: msg.content
      });

      // Fetch word timestamps JSON
      let tData = [];
      try {
        const tRes = await fetch(result.timestampsUrl);
        if (tRes.ok) {
          tData = await tRes.json();
        }
      } catch (tErr) {
        console.error("Failed to fetch timestamps:", tErr);
      }

      setTimestamps(tData);
      setAudioUrl(result.audioUrl);
      setTimestampsUrl(result.timestampsUrl);
    } catch (err) {
      console.error("Failed speech gen:", err);
    } finally {
      setIsLoadingSpeech(false);
    }
  };

  const hasAutoplayedRef = useRef(false);

  useEffect(() => {
    if (msg.role !== 'assistant' || !msg.content || msg.content === '...') return;
    if (isAIResponding) return; // Wait until streaming finishes

    // ONLY auto-fetch if this is the latest message AND was just generated!
    if (isLatest && msg.justGenerated && !hasAutoplayedRef.current) {
      hasAutoplayedRef.current = true;
      msg.justGenerated = false; // Disable further autoplaying
      fetchSpeech(true);
    }
  }, [msg.content, msg.role, isAIResponding, isLatest, msg.justGenerated]);

  useEffect(() => {
    if (!audioUrl) return;

    const audio = new Audio(audioUrl);
    audioRef.current = audio;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      setHighlightedWordIndex(-1);
    };

    const handleTimeUpdate = () => {
      if (!audio) return;
      const curTime = audio.currentTime;

      const idx = timestampsRef.current.findIndex((t, i) => {
        const next = timestampsRef.current[i + 1];
        return curTime >= t.start && (!next || curTime < next.start);
      });
      setHighlightedWordIndex(idx);
    };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('timeupdate', handleTimeUpdate);

    // Play the audio and dispatch event to pause others
    window.dispatchEvent(new CustomEvent('jarvis-speech-play', { detail: { audioRef: audio } }));
    audio.play().catch(err => {
      console.warn("Autoplay blocked or failed:", err);
    });

    return () => {
      audio.pause();
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audioRef.current = null;
    };
  }, [audioUrl]);

  // Exclusive sequential playback listener
  useEffect(() => {
    const handleSpeechPlay = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (audioRef.current && audioRef.current !== customEvent.detail.audioRef) {
        audioRef.current.pause();
      }
    };

    window.addEventListener('jarvis-speech-play', handleSpeechPlay);
    return () => {
      window.removeEventListener('jarvis-speech-play', handleSpeechPlay);
    };
  }, []);

  const carouselRef = useRef<HTMLDivElement | null>(null);

  const scrollCarousel = (direction: 'left' | 'right') => {
    if (!carouselRef.current) return;
    const container = carouselRef.current;
    const scrollAmount = container.clientWidth * 0.75;
    const targetScroll = direction === 'left' 
      ? container.scrollLeft - scrollAmount 
      : container.scrollLeft + scrollAmount;
    container.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });
  };

  const togglePlayback = () => {
    if (audioUrl) {
      if (!audioRef.current) return;
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        window.dispatchEvent(new CustomEvent('jarvis-speech-play', { detail: { audioRef: audioRef.current } }));
        audioRef.current.play().catch(err => console.warn(err));
      }
    } else {
      fetchSpeech(true);
    }
  };

  const handleCopy = () => {
    const cleaned = msg.content.replace(/【\d+(?:†[^】]*)?】/g, '');
    navigator.clipboard.writeText(cleaned);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={cn(
      "leading-relaxed p-2.5 rounded-xl border font-mono transition-all relative group/msg",
      textSize === 'sm'
        ? "text-[10px] [&_p]:text-[10px] [&_li]:text-[10px] [&_td]:text-[10px] [&_th]:text-[10px] [&_code]:text-[9px]"
        : textSize === 'lg'
          ? "text-[14px] [&_p]:text-[14px] [&_li]:text-[14px] [&_td]:text-[14px] [&_th]:text-[14px] [&_code]:text-[13px]"
          : "text-xs [&_p]:text-xs [&_li]:text-xs [&_td]:text-xs [&_th]:text-xs [&_code]:text-[11px]",
      msg.role === 'user'
        ? "bg-[#34343490] text-zinc-200 border-[#141414]/15 rounded-br-none self-end max-w-[85%] w-fit"
        : "bg-transparent text-zinc-300 border-transparent self-start max-w-[90%] w-fit"
    )}>
      <style>{`
        .speech-playing .speech-word {
          opacity: 0.6;
          transition: all 0.15s ease;
        }
        .speech-playing .speech-word.speech-word-active {
          opacity: 1;
          color: #60a5fa;
          font-weight: 600;
          text-decoration: underline;
          text-decoration-color: rgba(59, 130, 246, 0.4);
        }
      `}</style>
      {msg.role === 'assistant' && msg.content && msg.content !== '...' && (
        <div className="absolute -bottom-4 left-2 flex items-center gap-1.5 opacity-0 group-hover/msg:opacity-100 transition-opacity z-10">
          {isLoadingSpeech ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin text-zinc-500" />
          ) : (
            <button
              type="button"
              onClick={togglePlayback}
              className="p-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 rounded-lg border border-zinc-700/50"
              title={isPlaying ? "Pause voice transmission" : "Play voice transmission"}
            >
              {isPlaying ? <Pause className="w-3.5 h-3.5 text-blue-400" /> : <Play className="w-3.5 h-3.5" />}
            </button>
          )}
          <button
            type="button"
            onClick={handleCopy}
            className="p-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 rounded-lg border border-zinc-700/50"
            title="Copy message text"
          >
            {copied ? (
              <Check className="w-3.5 h-3.5 text-green-500" />
            ) : (
              <Copy className="w-3.5 h-3.5" />
            )}
          </button>
        </div>
      )}

      <div
        ref={containerRef}
        className={cn(
          "prose prose-invert prose-xs max-w-none break-words [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 pr-1",
          textSize === 'sm'
            ? "text-[10px] [&_p]:text-[10px] [&_li]:text-[10px] [&_td]:text-[10px] [&_th]:text-[10px] [&_code]:text-[9px] [&_span]:text-[10px]"
            : textSize === 'lg'
              ? "text-[14px] [&_p]:text-[14px] [&_li]:text-[14px] [&_td]:text-[14px] [&_th]:text-[14px] [&_code]:text-[13px] [&_span]:text-[14px]"
              : "text-xs [&_p]:text-xs [&_li]:text-xs [&_td]:text-xs [&_th]:text-xs [&_code]:text-[11px] [&_span]:text-xs",
          isPlaying && "speech-playing"
        )}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeWordWrap]}
          components={markdownComponents}
        >
          {msg.content ? msg.content.replace(/【\d+(?:†[^】]*)?】/g, '') : "..."}
        </ReactMarkdown>
      </div>

      {visibleImages && visibleImages.length > 0 && (
        <div className="mt-3 pt-2 border-t border-[#2d2d2d] w-full">
          <div className="text-[8px] text-zinc-500 tracking-wider mb-1.5 uppercase font-bold flex items-center gap-1.5">
            <Globe2 className="w-3 h-3 text-blue-500" />
            RECONNAISSANCE IMAGES:
          </div>

          <div className="relative group/carousel w-full max-w-full">
            {/* Left Scroll Button */}
            <button
              type="button"
              onClick={() => scrollCarousel('left')}
              className="absolute left-1.5 top-1/2 -translate-y-1/2 bg-[#121212]/80 hover:bg-blue-600 text-zinc-400 hover:text-white p-1 rounded-md border border-[#2d2d2d] hover:border-blue-400 transition-all opacity-0 group-hover/carousel:opacity-100 duration-200 z-10 hidden sm:flex items-center justify-center active:scale-90"
              title="Previous Image"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {/* Right Scroll Button */}
            <button
              type="button"
              onClick={() => scrollCarousel('right')}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-[#121212]/80 hover:bg-blue-600 text-zinc-400 hover:text-white p-1 rounded-md border border-[#2d2d2d] hover:border-blue-400 transition-all opacity-0 group-hover/carousel:opacity-100 duration-200 z-10 hidden sm:flex items-center justify-center active:scale-90"
              title="Next Image"
            >
              <ChevronRight className="w-4 h-4" />
            </button>

            {/* Scroll Container */}
            <div
              ref={carouselRef}
              className="flex gap-2 overflow-x-auto pb-1.5 scroll-smooth snap-x snap-mandatory custom-scrollbar select-none"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {visibleImages.map((imgUrl, imgIdx) => (
                <div
                  key={imgIdx}
                  onClick={() => setLightboxImage(imgUrl)}
                  className="relative flex-shrink-0 w-32 h-20 rounded-lg border border-[#2d2d2d] overflow-hidden cursor-pointer hover:border-blue-500/50 transition-colors snap-start group/img bg-[#121212]"
                >
                  <img
                    src={imgUrl}
                    alt={`recon-${imgIdx}`}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover/img:scale-105"
                    loading="lazy"
                    draggable={false}
                    onError={() => {
                      setFailedImages(prev => ({ ...prev, [imgUrl]: true }));
                    }}
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center">
                    <Maximize2 className="w-3.5 h-3.5 text-white" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const fetchLandmarkImages = async (countryName: string): Promise<string[]> => {
  try {
    const url = `https://en.wikipedia.org/api/rest_v1/page/media-list/${encodeURIComponent(countryName)}`;
    const res = await fetch(url);
    if (res.ok) {
      const data = await res.json();
      if (data.items && Array.isArray(data.items)) {
        const imageUrls: string[] = [];
        for (const item of data.items) {
          if (item.type === 'image' && item.srcset && item.srcset.length > 0) {
            let src = item.srcset[item.srcset.length - 1].src;
            if (src) {
              if (src.startsWith('//')) {
                src = 'https:' + src;
              }
              // Filter out SVG files and icons
              if (src.toLowerCase().includes('.svg') || src.toLowerCase().includes('icon')) {
                continue;
              }
              if (/^https?:\/\//i.test(src)) {
                imageUrls.push(src);
              }
            }
          }
          if (imageUrls.length >= 6) break;
        }
        return imageUrls;
      }
    }
  } catch (err) {
  }
  return [];
};

const getJarvisSystemPrompt = () => ({
  role: 'system' as const,
  content: `You are J.A.R.V.I.S., a world-class AI assistant by Stark Industries.
Rules:
- Format in clean markdown (**bold**, lists, headings). No HTML tags.
- Keep responses concise, tactical, and data-driven.
- If search results are provided, prioritize them.
- Do not output citation brackets or search footnotes.
- MAP FOCUS COMMANDS: If the user requests to focus, zoom, locate, or show a specific country or state/province on the map, you MUST prepend a command tag at the very beginning of your response.
  Format: [MAP_COMMAND: {"action": "focus", "type": "country"|"state", "target": "Name of Target", "country": "Name of Parent Country (only if type is state)"}]
  Examples:
  - User: "focus India" -> Response: "[MAP_COMMAND: {\"action\": \"focus\", \"type\": \"country\", \"target\": \"India\"}] Understood, focusing on India."
  - User: "zoom on Maharashtra" -> Response: "[MAP_COMMAND: {\"action\": \"focus\", \"type\": \"state\", \"target\": \"Maharashtra\", \"country\": \"India\"}] Centering satellite scan on Maharashtra, India."
  Make sure this tag is at the very beginning of your output, followed by your conversational reply.`
});

const getTelemetryRetrievalPrompt = (name: string, isState: boolean) => {
  if (isState) {
    return `Return a JSON object for state/division "${name}" using this schema:
{
  "president": "Current Governor, Mayor, or Head name",
  "pm": "Current Chief Minister (CM) or Premier name",
  "dictator": null,
  "strengths": ["exactly 3 strengths"],
  "weaknesses": ["exactly 3 weaknesses"]
}`;
  } else {
    return `Return a JSON object for country "${name}" using this schema:
{
  "president": "Current President/Monarch/Head of State name",
  "pm": "Current Prime Minister/Head of Government name",
  "dictator": "Dictator/supreme leader name if applicable, else null",
  "strengths": ["exactly 3 strengths"],
  "weaknesses": ["exactly 3 weaknesses"]
}`;
  }
};

const fetchLeaderImage = async (leaderName: string): Promise<string | null> => {
  if (!leaderName || leaderName === 'N/A' || leaderName.includes('Executive President') || leaderName.includes('Prime Minister') || leaderName.includes('Chief Minister')) {
    return null;
  }
  try {
    const cleanName = leaderName.replace(/\s*\(.*\)/g, '').trim();
    // Try Tavily search for leader image first
    try {
      const tavilyRes = await fetch('https://api.tavily.com/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          api_key: "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",
          query: `${cleanName} official portrait photo`,
          search_depth: "fast",
          include_images: true,
          max_results: 1
        })
      });
      if (tavilyRes.ok) {
        const data = await tavilyRes.json();
        if (data.images && data.images.length > 0 && data.images[0]) {
          return data.images[0];
        }
      }
    } catch (tErr) {
      console.warn('Tavily leader image fetch failed, falling back to Wikipedia:', tErr);
    }

    const url = `https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(cleanName)}`;
    const res = await fetch(url);
    if (res.ok) {
      const data = await res.json();
      return data.thumbnail?.source || null;
    }
  } catch (err) {
    console.error('Error fetching leader image:', err);
  }
  return null;
};

const fetchMoreInfoFromAI = async (name: string, isState: boolean): Promise<any> => {
  const systemPrompt = getTelemetryRetrievalPrompt(name, isState);

  // Query Tavily for real-time telemetry details
  let tavilyContext = '';
  try {
    const query = isState
      ? `current governor, chief minister, strengths, and weaknesses of the state ${name}`
      : `current president, prime minister, strengths, and weaknesses of the country ${name}`;

    const tavilyRes = await fetch('https://api.tavily.com/search', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        api_key: "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",
        query: query,
        include_answer: "advanced",
        search_depth: "fast"
      })
    });
    if (tavilyRes.ok) {
      const tData = await tavilyRes.json();
      tavilyContext = tData.answer || tData.results?.map((r: any) => r.content).join('\n') || '';
    }
  } catch (err) {
    console.error('Tavily telemetry fetch failed:', err);
  }

  let attempts = 0;
  const maxAttempts = GROQ_API_KEYS.length;
  let responseText = '';

  while (attempts < maxAttempts) {
    try {
      const res = await fetch('https://api.groq.com/openai/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getGroqApiKey()}`
        },
        body: JSON.stringify({
          messages: [
            { role: 'system', content: systemPrompt },
            { 
              role: 'user', 
              content: `Retrieve data for: ${name}.${tavilyContext ? ` Here is up-to-date web search context:\n\n${tavilyContext}` : ''}` 
            }
          ],
          model: "openai/gpt-oss-120b",
          temperature: 0.1,
          response_format: { type: "json_object" }
        })
      });

      if (res.status === 429) {
        rotateGroqApiKey();
        attempts++;
        continue;
      }

      if (res.ok) {
        const data = await res.json();
        responseText = data.choices?.[0]?.message?.content || '';
        break;
      } else {
        rotateGroqApiKey();
        attempts++;
      }
    } catch (e) {
      rotateGroqApiKey();
      attempts++;
    }
  }

  if (!responseText) {
    throw new Error('Failed to fetch from Groq');
  }

  try {
    const parsed = JSON.parse(responseText);
    const result = {
      president: parsed.president || `${name} Leader`,
      pm: parsed.pm || 'N/A',
      dictator: parsed.dictator || null,
      strengths: Array.isArray(parsed.strengths) ? parsed.strengths.slice(0, 3) : ['Strategic assets', 'Resource availability', 'Geographic position'],
      weaknesses: Array.isArray(parsed.weaknesses) ? parsed.weaknesses.slice(0, 3) : ['Economic adjustment', 'Infrastructure bottlenecks', 'Systemic pressures']
    };

    const [presidentImage, pmImage] = await Promise.all([
      fetchLeaderImage(result.president),
      fetchLeaderImage(result.pm)
    ]);

    return {
      ...result,
      presidentImage,
      pmImage
    };
  } catch (err) {
    console.error('Error parsing AI response:', err);
    throw err;
  }
};



export const OG: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterMode>('weather');
  const globeRef = useRef<JarvisGlobeRef>(null);

  const [countriesList, setCountriesList] = useState<any[]>([]);
  const executedCommandRef = useRef<boolean>(false);
  const pendingStateFocusRef = useRef<{ name: string } | null>(null);

  useEffect(() => {
    fetch('/ne_110m_admin_0_countries.json')
      .then(r => r.ok ? r.json() : { features: [] })
      .then(geoD => {
        const features = geoD.features || [];
        const parsed = features.map((f: any) => {
          const prop = f.properties;
          let lat = 0;
          let lng = 0;
          if (f.bbox && f.bbox.length === 4) {
            lng = (f.bbox[0] + f.bbox[2]) / 2;
            lat = (f.bbox[1] + f.bbox[3]) / 2;
          } else if (f.geometry && f.geometry.coordinates) {
            const coords = f.geometry.type === 'Polygon'
              ? f.geometry.coordinates[0][0]
              : f.geometry.coordinates[0][0][0];
            if (coords && coords.length === 2) {
              lng = coords[0];
              lat = coords[1];
            }
          }

          const cca2 = prop.ISO_A2 && prop.ISO_A2 !== '-99' ? String(prop.ISO_A2).trim().toUpperCase() : prop.POSTAL || '';
          const cca3 = prop.ISO_A3 && prop.ISO_A3 !== '-99' ? String(prop.ISO_A3).trim().toUpperCase() : prop.ADM0_A3 || '';
          const name = prop.NAME || prop.ADMIN || 'Unknown Region';

          return {
            lat,
            lng,
            name,
            cca2,
            cca3,
            flag: cca2 && cca2 !== '-99' ? `https://flagcdn.com/${cca2.toLowerCase()}.svg` : '',
            capital: `${name} Capital`,
            population: prop.POP_EST || 1000000,
            region: prop.CONTINENT || prop.REGION_UN || 'Global',
            area: prop.AREA || 120000,
            gdp: prop.GDP_MD_EST ? prop.GDP_MD_EST * 1e6 : 1e9,
            languages: 'English, Native',
            currencies: 'Local Unit',
            timezones: 'UTC'
          };
        });
        setCountriesList(parsed);
      })
      .catch(() => { });
  }, []);

  // Right Panel HUD Controls State
  const [rightPanelExpanded, setRightPanelExpanded] = useState(true);
  const [leftPanelExpanded, setLeftPanelExpanded] = useState(true);
  const [isLocating, setIsLocating] = useState(false);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);

  // Chat Tab State
  const [chatTabs, setChatTabs] = useState<ChatTab[]>([
    {
      id: 'tab-1',
      title: 'TAB 1',
      messages: [],
      isResponding: false,
      statusText: 'PROCESSING...'
    }
  ]);
  const [activeTabId, setActiveTabId] = useState<string>('tab-1');
  const [tabPendingClose, setTabPendingClose] = useState<string | null>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  // Derived Active Tab State
  const activeTab = useMemo(() => {
    return chatTabs.find(t => t.id === activeTabId) || chatTabs[0];
  }, [chatTabs, activeTabId]);

  const chatMessages = activeTab.messages;
  const isAIResponding = activeTab.isResponding;
  const aiStatusText = activeTab.statusText;

  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  // OS Windows State
  const [windows, setWindows] = useState<OSWindow[]>([]);

  useEffect(() => {
    setWindows([{
      id: 'chat-main',
      type: 'chat',
      title: '',
      x: Math.max(40, typeof window !== 'undefined' ? window.innerWidth / 2 - 250 : 100),
      y: 80,
      maximized: typeof window !== 'undefined' && window.innerWidth < 640
    }]);
  }, []);

  const [activeWindowId, setActiveWindowId] = useState<string>('chat-main');
  const [selectedCountryName, setSelectedCountryName] = useState<string | null>(null);
  const [selectedCountryData, setSelectedCountryData] = useState<any | null>(null);

  // User and Settings States
  const [showStateNames, setShowStateNames] = useState(true);
  const [textSize, setTextSize] = useState<'sm' | 'md' | 'lg'>('md');
  const [showTaskbar, setShowTaskbar] = useState(false);
  const [countryStates, setCountryStates] = useState<any[]>([]);
  const [menuOpenWindowId, setMenuOpenWindowId] = useState<string | null>(null);
  const [showStatesWindowId, setShowStatesWindowId] = useState<string | null>(null);
  const [showMoreInfoWindowId, setShowMoreInfoWindowId] = useState<string | null>(null);

  // State updaters
  const setChatMessages = useCallback((updater: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => {
    setChatTabs(prev => prev.map(t => {
      if (t.id === activeTabId) {
        const nextMsgs = typeof updater === 'function' ? updater(t.messages) : updater;
        return { ...t, messages: nextMsgs };
      }
      return t;
    }));
  }, [activeTabId]);

  const setTabResponding = useCallback((isResponding: boolean, tabId = activeTabId) => {
    setChatTabs(prev => prev.map(t => {
      if (t.id === tabId) {
        return { ...t, isResponding };
      }
      return t;
    }));
  }, [activeTabId]);

  const setTabStatusText = useCallback((statusText: string, tabId = activeTabId) => {
    setChatTabs(prev => prev.map(t => {
      if (t.id === tabId) {
        return { ...t, statusText };
      }
      return t;
    }));
  }, [activeTabId]);

  // Chat State
  const [queryInput, setQueryInput] = useState<string>('');
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea and reset when cleared
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      if (!queryInput) {
        textarea.style.height = 'auto';
      } else {
        textarea.style.height = 'auto';
        textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
      }
    }
  }, [queryInput]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      if (queryInput.trim() && !isAIResponding) {
        submitChatQuery(queryInput);
      }
    }
  };

  const handleCopyMessage = useCallback((text: string, index: number) => {
    const cleaned = text.replace(/【\d+(?:†[^】]*)?】/g, '');
    navigator.clipboard.writeText(cleaned);
    setCopiedIndex(index);
    setTimeout(() => {
      setCopiedIndex(current => current === index ? null : current);
    }, 2000);
  }, []);

  // Markdown components matching main chat page formatting
  const markdownComponents: Components = useMemo(() => ({
    table: ({ node, ...props }) => (
      <div className="my-2 overflow-x-auto rounded border border-[#2d2d2d] bg-[#141414] shadow-sm markdown-table-container w-fit max-w-full">
        <table className="min-w-max border-collapse font-mono text-[0.9em]" {...props} />
      </div>
    ),
    thead: ({ node, ...props }) => <thead className="bg-[#1c1c1c] border-b border-[#2d2d2d]" {...props} />,
    tbody: ({ node, ...props }) => <tbody className="divide-y divide-[#2d2d2d]" {...props} />,
    tr: ({ node, ...props }) => <tr className="transition-colors hover:bg-zinc-800/30" {...props} />,
    th: ({ node, ...props }) => (
      <th className="px-3 py-1.5 text-left font-semibold text-zinc-300 border-b border-[#2d2d2d]" {...props} />
    ),
    td: ({ node, ...props }) => (
      <td className="px-3 py-1.5 text-zinc-400 border-b border-zinc-800/50 last:border-b-0" {...props} />
    ),
    code({ node, className, children, ...props }: any) {
      const match = /language-(\w+)/.exec(className || '');
      const isCodeBlock = match !== null;
      if (isCodeBlock) {
        return (
          <pre className="rounded bg-[#121212] p-2.5 border border-[#2d2d2d] my-2 overflow-x-auto text-[0.85em]">
            <code className="text-zinc-300 font-mono" {...props}>{children}</code>
          </pre>
        );
      }
      return (
        <code className="rounded bg-zinc-800/60 px-1 py-0.5 text-[0.85em] font-mono text-zinc-300 border border-zinc-700/35" {...props}>
          {children}
        </code>
      );
    },
    a: ({ node, href = '', children, ...props }) => (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 hover:underline underline-offset-2 transition-colors font-semibold" {...props}>
        {children}
      </a>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-2 border-blue-500/40 pl-3.5 italic my-2 bg-zinc-900/40 py-1.5 rounded-r text-zinc-400 text-[0.9em]">
        {children}
      </blockquote>
    ),
    ul: ({ children }) => <ul className="list-disc pl-4 mb-2 space-y-0.5 text-[0.95em]">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal pl-4 mb-2 space-y-0.5 text-[0.95em]">{children}</ol>,
    li: ({ children }) => <li className="text-zinc-300 leading-normal font-mono">{children}</li>,
    h1: ({ children }) => <h1 className="text-[1.15em] font-bold mb-1.5 text-zinc-200 border-b border-[#2d2d2d] pb-0.5 font-mono uppercase tracking-wider">{children}</h1>,
    h2: ({ children }) => <h2 className="text-[1.05em] font-semibold mb-1 text-zinc-300 font-mono uppercase tracking-wider">{children}</h2>,
    h3: ({ children }) => <h3 className="text-[0.95em] font-medium mb-1 text-zinc-300 font-mono">{children}</h3>,
    p: ({ children }) => <p className="mb-1.5 text-zinc-300/90 leading-relaxed font-mono">{children}</p>,
    strong: ({ children }) => <strong className="font-semibold text-zinc-200">{children}</strong>,
    em: ({ children }) => <em className="italic text-zinc-300/70">{children}</em>,
    hr: () => <hr className="my-2.5 border-[#2d2d2d]" />,
  }), []);

  // Auto scroll chat
  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [chatMessages]);

  const updateWindow = useCallback((id: string, updates: Partial<OSWindow>) => {
    setWindows(prev => prev.map(w => w.id === id ? { ...w, ...updates } : w));
  }, []);

  const closeWindow = useCallback((id: string) => {
    setWindows(prev => prev.filter(w => w.id !== id));
  }, []);

  const focusWindow = useCallback((id: string) => {
    setActiveWindowId(id);
  }, []);

  const openChatWindow = useCallback(() => {
    setWindows(prev => {
      if (!prev.find(w => w.type === 'chat')) {
        return [...prev, {
          id: 'chat-main',
          type: 'chat',
          title: '',
          x: Math.max(40, window.innerWidth / 2 - Math.min(window.innerWidth * 0.9, 500) / 2),
          y: 80,
          maximized: window.innerWidth < 640
        }];
      }
      return prev;
    });
    focusWindow('chat-main');
  }, [focusWindow]);

  const openSettingsWindow = useCallback(() => {
    setWindows(prev => {
      if (!prev.find(w => w.id === 'settings-main')) {
        return [...prev, {
          id: 'settings-main',
          type: 'settings' as any,
          title: 'SYSTEM SETTINGS',
          x: Math.max(40, window.innerWidth / 2 - 200),
          y: 120,
          maximized: window.innerWidth < 640
        }];
      }
      return prev;
    });
    focusWindow('settings-main');
  }, [focusWindow]);

  const handleAddTab = useCallback(() => {
    const nextId = `tab-${Date.now()}`;
    setChatTabs(prev => {
      if (prev.length >= 3) return prev;
      const nextNum = prev.length + 1;
      return [
        ...prev,
        {
          id: nextId,
          title: `TAB ${nextNum}`,
          messages: [],
          isResponding: false,
          statusText: 'PROCESSING...'
        }
      ];
    });
    setActiveTabId(nextId);
  }, []);

  const executeCloseTab = useCallback((tabId: string) => {
    setChatTabs(prev => {
      if (prev.length <= 1) return prev;
      const nextTabs = prev.filter(t => t.id !== tabId);
      if (activeTabId === tabId) {
        const remainingTab = nextTabs[0];
        if (remainingTab) {
          setActiveTabId(remainingTab.id);
        }
      }
      return nextTabs;
    });
    setTabPendingClose(null);
  }, [activeTabId]);

  const handleCloseTab = useCallback((tabId: string) => {
    const tab = chatTabs.find(t => t.id === tabId);
    if (tab && tab.messages.length > 0) {
      setTabPendingClose(tabId);
    } else {
      executeCloseTab(tabId);
    }
  }, [chatTabs, executeCloseTab]);

  const handleWindowClose = useCallback((id: string) => {
    closeWindow(id);
  }, [closeWindow]);



  // Helper to fetch country info in background if country window is recreated
  const fetchCountryInfoInBackground = useCallback(async (winId: string, countryData: any) => {
    try {
      let wikiData = null;
      let weatherInfo = null;
      let landmarkImages: string[] = [];
      let moreInfoData = null;

      const [wikiRes, wRes, landmarks, moreInfo] = await Promise.allSettled([
        fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(countryData.name)}`),
        fetch(`https://api.open-meteo.com/v1/forecast?latitude=${countryData.lat}&longitude=${countryData.lng}&current_weather=true`),
        fetchLandmarkImages(countryData.name),
        fetchMoreInfoFromAI(countryData.name, false)
      ]);

      if (wikiRes.status === 'fulfilled' && wikiRes.value.ok) {
        wikiData = await wikiRes.value.json();
      }
      if (wRes.status === 'fulfilled' && wRes.value.ok) {
        weatherInfo = await wRes.value.json();
      }
      if (landmarks.status === 'fulfilled') {
        landmarkImages = landmarks.value;
      }
      if (moreInfo.status === 'fulfilled') {
        moreInfoData = moreInfo.value;
      } else {
        moreInfoData = {
          president: `${countryData.name} President`,
          pm: `${countryData.name} Prime Minister`,
          dictator: null,
          strengths: ['Strategic trade core', 'Cultural tourism assets', 'Resilient agricultural core'],
          weaknesses: ['Vulnerability to global shocks', 'Infrastructure investment gaps', 'Fiscal adjustment constraints']
        };
      }

      setWindows(prev => prev.map(w => {
        if (w.id === winId) {
          const updatedTabs = w.data.tabs.map((t: any) => t.id === 'country' ? {
            ...t,
            data: {
              ...t.data,
              loadingInfo: false,
              loadingMoreInfo: false,
              wiki: wikiData,
              weather: weatherInfo?.current_weather,
              landmarks: landmarkImages,
              moreInfo: moreInfoData
            }
          } : t);
          return {
            ...w,
            data: {
              ...w.data,
              tabs: updatedTabs
            }
          };
        }
        return w;
      }));
    } catch (err) {
      setWindows(prev => prev.map(w => {
        if (w.id === winId) {
          const updatedTabs = w.data.tabs.map((t: any) => t.id === 'country' ? {
            ...t,
            data: {
              ...t.data,
              loadingInfo: false,
              loadingMoreInfo: false,
              error: true
            }
          } : t);
          return {
            ...w,
            data: {
              ...w.data,
              tabs: updatedTabs
            }
          };
        }
        return w;
      }));
    }
  }, []);

  // Handle Map Country Selection
  const handleCountrySelect = useCallback(async (countryData: any) => {
    setCountryStates([]);
    setShowStatesWindowId(null);
    setShowMoreInfoWindowId(null);
    setMenuOpenWindowId(null);

    if (!countryData) {
      setSelectedCountryName(null);
      setSelectedCountryData(null);
      return;
    }

    setSelectedCountryName(countryData.name);
    setSelectedCountryData(countryData);

    const winId = `info-${countryData.name}`;
    setWindows(prev => {
      const existing = prev.find(w => w.id === winId);
      if (existing) {
        const updatedData = {
          ...existing.data,
          activeTabId: 'country'
        };
        focusWindow(winId);
        return prev.map(w => w.id === winId ? { ...w, data: updatedData } : w);
      }

      const newWin: OSWindow = {
        id: winId,
        type: 'info',
        title: `${countryData.name.toUpperCase()}`,
        x: Math.max(40, window.innerWidth / 2 - Math.min(window.innerWidth * 0.9, 500) / 2),
        y: Math.max(50, window.innerHeight / 2 - Math.min(window.innerHeight * 0.8, 420) / 2),
        maximized: window.innerWidth < 640,
        data: {
          countryName: countryData.name,
          activeTabId: 'country',
          tabs: [
            {
              id: 'country',
              type: 'country',
              title: countryData.name,
              flag: countryData.flag,
              data: { ...countryData, loadingInfo: true, loadingMoreInfo: true }
            }
          ]
        }
      };
      return [...prev, newWin];
    });
    focusWindow(winId);

    try {
      let wikiData = null;
      let weatherInfo = null;
      let landmarkImages: string[] = [];
      let moreInfoData = null;

      const [wikiRes, wRes, landmarks, moreInfo] = await Promise.allSettled([
        fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(countryData.name)}`),
        fetch(`https://api.open-meteo.com/v1/forecast?latitude=${countryData.lat}&longitude=${countryData.lng}&current_weather=true`),
        fetchLandmarkImages(countryData.name),
        fetchMoreInfoFromAI(countryData.name, false)
      ]);

      if (wikiRes.status === 'fulfilled' && wikiRes.value.ok) {
        wikiData = await wikiRes.value.json();
      }
      if (wRes.status === 'fulfilled' && wRes.value.ok) {
        weatherInfo = await wRes.value.json();
      }
      if (landmarks.status === 'fulfilled') {
        landmarkImages = landmarks.value;
      }
      if (moreInfo.status === 'fulfilled') {
        moreInfoData = moreInfo.value;
      } else {
        moreInfoData = {
          president: `${countryData.name} President`,
          pm: `${countryData.name} Prime Minister`,
          dictator: null,
          strengths: ['Strategic trade core', 'Cultural tourism assets', 'Resilient agricultural core'],
          weaknesses: ['Vulnerability to global shocks', 'Infrastructure investment gaps', 'Fiscal adjustment constraints']
        };
      }

      setWindows(prev => prev.map(w => {
        if (w.id === winId) {
          const updatedTabs = w.data.tabs.map((t: any) => t.id === 'country' ? {
            ...t,
            data: {
              ...t.data,
              loadingInfo: false,
              loadingMoreInfo: false,
              wiki: wikiData,
              weather: weatherInfo?.current_weather,
              landmarks: landmarkImages,
              moreInfo: moreInfoData
            }
          } : t);
          return {
            ...w,
            data: {
              ...w.data,
              tabs: updatedTabs
            }
          };
        }
        return w;
      }));

    } catch (err) {
      setWindows(prev => prev.map(w => {
        if (w.id === winId) {
          const updatedTabs = w.data.tabs.map((t: any) => t.id === 'country' ? {
            ...t,
            data: {
              ...t.data,
              loadingInfo: false,
              loadingMoreInfo: false,
              error: true
            }
          } : t);
          return {
            ...w,
            data: {
              ...w.data,
              tabs: updatedTabs
            }
          };
        }
        return w;
      }));
    }
  }, [focusWindow]);

  // Handle Map State Selection
  const handleStateSelect = useCallback(async (stateData: any) => {
    if (!stateData) return;

    const countryName = stateData.country || selectedCountryName;
    if (!countryName) return;

    const winId = `info-${countryName}`;
    const stateTabId = `state-${stateData.name}`;

    setWindows(prev => {
      const existing = prev.find(w => w.id === winId);
      if (existing) {
        const tabExists = existing.data.tabs.some((t: any) => t.id === stateTabId);
        let updatedTabs = [...existing.data.tabs];
        if (!tabExists) {
          updatedTabs.push({
            id: stateTabId,
            type: 'state',
            title: stateData.name,
            data: {
              ...stateData,
              capital: countryName,
              region: 'State Territory',
              population: 0,
              area: 0,
              loadingInfo: true,
              loadingMoreInfo: true
            }
          });
        }
        const updatedData = {
          ...existing.data,
          activeTabId: stateTabId,
          tabs: updatedTabs
        };
        focusWindow(winId);
        return prev.map(w => w.id === winId ? { ...w, data: updatedData } : w);
      } else {
        const newWin: OSWindow = {
          id: winId,
          type: 'info',
          title: `${countryName.toUpperCase()}`,
          x: Math.max(40, window.innerWidth / 2 - Math.min(window.innerWidth * 0.9, 500) / 2),
          y: Math.max(50, window.innerHeight / 2 - Math.min(window.innerHeight * 0.8, 420) / 2),
          maximized: window.innerWidth < 640,
          data: {
            countryName: countryName,
            activeTabId: stateTabId,
            tabs: [
              {
                id: 'country',
                type: 'country',
                title: countryName,
                flag: selectedCountryData?.flag || '',
                data: { ...selectedCountryData, loadingInfo: true, loadingMoreInfo: true }
              },
              {
                id: stateTabId,
                type: 'state',
                title: stateData.name,
                data: {
                  ...stateData,
                  capital: countryName,
                  region: 'State Territory',
                  population: 0,
                  area: 0,
                  loadingInfo: true,
                  loadingMoreInfo: true
                }
              }
            ]
          }
        };
        focusWindow(winId);
        if (selectedCountryData) {
          fetchCountryInfoInBackground(winId, selectedCountryData);
        }
        return [...prev, newWin];
      }
    });
    focusWindow(winId);

    // Fetch Wikipedia Facts, Weather & Landmarks for State
    try {
      let wikiData = null;
      let weatherInfo = null;
      let landmarkImages: string[] = [];
      let moreInfoData = null;

      const [wikiRes, weatherData, landmarks, moreInfo] = await Promise.allSettled([
        fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(stateData.name)}`),
        stateData.lat !== undefined && stateData.lng !== undefined
          ? fetchJarvisWeather(`${stateData.lat},${stateData.lng}`)
          : Promise.reject('No coordinates'),
        fetchLandmarkImages(stateData.name),
        fetchMoreInfoFromAI(stateData.name, true)
      ]);

      if (wikiRes.status === 'fulfilled' && wikiRes.value.ok) {
        wikiData = await wikiRes.value.json();
      }
      if (weatherData.status === 'fulfilled' && weatherData.value) {
        const wVal = weatherData.value;
        weatherInfo = {
          temperature: wVal.temperature,
          windspeed: wVal.windSpeed,
          weatherDesc: wVal.condition,
          humidity: wVal.humidity,
          aqi: wVal.aqi,
          airQuality: wVal.airQuality
        };
      }
      if (landmarks.status === 'fulfilled') {
        landmarkImages = landmarks.value;
      }
      if (moreInfo.status === 'fulfilled') {
        moreInfoData = moreInfo.value;
      } else {
        moreInfoData = {
          president: `${stateData.name} Governor`,
          pm: `${stateData.name} Chief Minister / Premier`,
          dictator: null,
          strengths: ['Local regional commerce', 'Rich cultural assets', 'Resilient agricultural core'],
          weaknesses: ['Infrastructure bottlenecks', 'Regional budget constraints', 'Environmental vulnerability']
        };
      }

      setWindows(prev => prev.map(w => {
        if (w.id === winId) {
          const updatedTabs = w.data.tabs.map((t: any) => t.id === stateTabId ? {
            ...t,
            data: {
              ...t.data,
              loadingInfo: false,
              loadingMoreInfo: false,
              wiki: wikiData,
              weather: weatherInfo,
              landmarks: landmarkImages,
              moreInfo: moreInfoData
            }
          } : t);
          return {
            ...w,
            data: {
              ...w.data,
              tabs: updatedTabs
            }
          };
        }
        return w;
      }));

    } catch (err) {
      setWindows(prev => prev.map(w => {
        if (w.id === winId) {
          const updatedTabs = w.data.tabs.map((t: any) => t.id === stateTabId ? {
            ...t,
            data: {
              ...t.data,
              loadingInfo: false,
              loadingMoreInfo: false,
              error: true
            }
          } : t);
          return {
            ...w,
            data: {
              ...w.data,
              tabs: updatedTabs
            }
          };
        }
        return w;
      }));
    }
  }, [focusWindow, selectedCountryName, selectedCountryData, fetchCountryInfoInBackground]);

  const handleStatesLoaded = useCallback((states: any[]) => {
    setCountryStates(states);

    if (pendingStateFocusRef.current) {
      const pending = pendingStateFocusRef.current;
      pendingStateFocusRef.current = null;
      const matchedState = states.find((s: any) => s.name.toLowerCase() === pending.name.toLowerCase());
      if (matchedState) {
        handleStateSelect(matchedState);
        if (matchedState.lat !== undefined && matchedState.lng !== undefined) {
          globeRef.current?.focusCoordinates(matchedState.lat, matchedState.lng, 4.5);
        }
      }
    }
  }, [handleStateSelect]);


  const executeMapCommand = useCallback((cmd: any) => {
    if (cmd.action === 'focus') {
      const { type, target, country } = cmd;
      if (type === 'country') {
        const matched = countriesList.find(c => c.name.toLowerCase() === target.toLowerCase());
        if (matched) {
          handleCountrySelect(matched);
        }
      } else if (type === 'state') {
        const parentCountryName = country || selectedCountryName;
        if (parentCountryName) {
          const matchedParent = countriesList.find(c => c.name.toLowerCase() === parentCountryName.toLowerCase());
          if (matchedParent) {
            handleCountrySelect(matchedParent);
            pendingStateFocusRef.current = { name: target };
          }
        }
      }
    }
  }, [countriesList, handleCountrySelect, selectedCountryName]);

  // Chat Execution with Groq Autonomous Function Calling and Streaming
  const submitChatQuery = useCallback(async (queryText: string) => {
    if (!queryText.trim() || activeTab.isResponding) return;

    executedCommandRef.current = false;

    // Check for immediate client-side match to zoom instantly
    const checkForImmediateCommand = (text: string) => {
      const clean = text.toLowerCase().trim().replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, "");
      const match = clean.match(/(?:jarvis\s+)?(?:focus\s+on|focus|zoom\s+to|show)\s+(.+)/i);
      if (match) {
        const target = match[1].trim();
        const matchedCountry = countriesList.find(c => c.name.toLowerCase() === target);
        if (matchedCountry) {
          handleCountrySelect(matchedCountry);
          return true;
        }
        const matchedState = countryStates.find(s => s.name.toLowerCase() === target);
        if (matchedState) {
          handleStateSelect(matchedState);
          if (matchedState.lat !== undefined && matchedState.lng !== undefined) {
            globeRef.current?.focusCoordinates(matchedState.lat, matchedState.lng, 4.5);
          }
          return true;
        }
      }
      return false;
    };

    checkForImmediateCommand(queryText);

    const targetTabId = activeTabId;
    setQueryInput('');
    setTabResponding(true, targetTabId);
    setTabStatusText('PROCESSING...', targetTabId);

    const userMessage = { role: 'user' as const, content: queryText };
    // Update messages for current tab
    setChatTabs(prev => prev.map(t => {
      if (t.id === targetTabId) {
        return { ...t, messages: [...t.messages, userMessage] };
      }
      return t;
    }));

    // Active System Prompt Instructions
    let systemPromptContent = getJarvisSystemPrompt().content;

    const systemPrompt = {
      role: 'system' as const,
      content: systemPromptContent
    };

    // Use current tab's message history for context
    const currentTabMessages = activeTab.messages;
    const historyBase = [...currentTabMessages, userMessage];
    const randomSeed = Math.floor(Math.random() * 1000000);

    try {
      // Formulate initial message payload
      const messagesToSend = [
        systemPrompt,
        ...historyBase.slice(-8).map(m => ({ role: m.role, content: m.content }))
      ];

      // Single Groq Call with built-in browser search and streaming, with key rotation on 429
      let res;
      let attempts = 0;
      const maxAttempts = GROQ_API_KEYS.length;

      while (attempts < maxAttempts) {
        res = await fetch('https://api.groq.com/openai/v1/chat/completions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getGroqApiKey()}`
          },
          body: JSON.stringify({
            messages: messagesToSend,
            model: "openai/gpt-oss-120b",
            temperature: 1,
            max_completion_tokens: 4096,
            top_p: 1,
            stream: true,
            reasoning_effort: "medium",
            stop: null,
            tools: [
              { type: 'browser_search' as const },
              { type: 'code_interpreter' as const }
            ],
            seed: randomSeed
          })
        });

        if (res.status === 429) {
          rotateGroqApiKey();
          attempts++;
          continue;
        }
        break;
      }

      if (!res || !res.ok) throw new Error(`API Error: ${res?.status}`);
      if (!res.body) throw new Error('Response body is null');

      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let assistantResponse = '';
      const images: string[] = [];

      // Append an empty assistant message first
      setChatTabs(prev => prev.map(t => {
        if (t.id === targetTabId) {
          return { 
            ...t, 
            messages: [
              ...t.messages, 
              { 
                role: 'assistant' as const, 
                content: '', 
                images: [],
                justGenerated: true 
              } 
            ] 
          };
        }
        return t;
      }));

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const cleaned = line.trim();
          if (!cleaned) continue;
          if (cleaned === 'data: [DONE]') continue;

          if (cleaned.startsWith('data: ')) {
            try {
              const parsedChunk = JSON.parse(cleaned.substring(6));
              const delta = parsedChunk.choices?.[0]?.delta;

              if (delta) {
                // If there's content delta, append it
                if (delta.content) {
                  assistantResponse += delta.content;

                  // Extract and execute MAP_COMMAND tag if present
                  const match = assistantResponse.match(/^\[MAP_COMMAND:\s*([^\]]+)\]/);
                  if (match) {
                    const jsonStr = match[1];
                    try {
                      const cmd = JSON.parse(jsonStr);
                      if (!executedCommandRef.current) {
                        executedCommandRef.current = true;
                        executeMapCommand(cmd);
                      }
                    } catch (e) {
                      // Keep waiting for complete JSON streaming to finish
                    }
                  }

                  const displayContent = assistantResponse.replace(/^\[MAP_COMMAND:\s*[^\]]+\]\s*/, '');

                  setChatTabs(prev => prev.map(t => {
                    if (t.id === targetTabId) {
                      const next = [...t.messages];
                      if (next.length > 0) {
                        const last = { ...next[next.length - 1] };
                        if (last.role === 'assistant') {
                          last.content = displayContent;
                          next[next.length - 1] = last;
                        }
                      }
                      return { ...t, messages: next };
                    }
                    return t;
                  }));
                }

                // Check for executed tools and images inside delta
                if (delta.executed_tools) {
                  delta.executed_tools.forEach((tool: any) => {
                    if (tool.search_results) {
                      tool.search_results.forEach((r: any) => {
                        if (r.images) {
                          r.images.forEach((img: any) => {
                            let url = '';
                            if (typeof img === 'string') url = img;
                            else if (img && img.url) url = img.url;
                            if (url && !images.includes(url)) {
                              images.push(url);
                            }
                          });
                        }
                      });
                    }
                  });
                }
              }

              // Also check parsedChunk level for executed_tools
              if (parsedChunk.executed_tools) {
                parsedChunk.executed_tools.forEach((tool: any) => {
                  if (tool.search_results) {
                    tool.search_results.forEach((r: any) => {
                      if (r.images) {
                        r.images.forEach((img: any) => {
                          let url = '';
                          if (typeof img === 'string') url = img;
                          else if (img && img.url) url = img.url;
                          if (url && !images.includes(url)) {
                            images.push(url);
                          }
                        });
                      }
                    });
                  }
                });
              }

              // Update images list on the message if we found any
              if (images.length > 0) {
                setChatTabs(prev => prev.map(t => {
                  if (t.id === targetTabId) {
                    const next = [...t.messages];
                    if (next.length > 0) {
                      const last = { ...next[next.length - 1] };
                      if (last.role === 'assistant') {
                        last.images = [...images];
                        next[next.length - 1] = last;
                      }
                    }
                    return { ...t, messages: next };
                  }
                  return t;
                }));
              }

            } catch (err) {
              // Ignore partial chunk parse issues
            }
          }
        }
      }

    } catch (err) {
      console.error(err);
      setChatTabs(prev => prev.map(t => {
        if (t.id === targetTabId) {
          return {
            ...t,
            messages: [
              ...t.messages,
              { role: 'assistant' as const, content: 'SYSTEM ERROR: Neural network uplink failed. Please retry.' }
            ]
          };
        }
        return t;
      }));
    } finally {
      setTabResponding(false, targetTabId);
    }
  }, [activeTab, activeTabId, setTabResponding, setTabStatusText, countriesList, handleCountrySelect, countryStates, handleStateSelect, executeMapCommand]);

  const handleCrisisSelect = useCallback(async (crisis: any) => {
    const winId = `crisis-${crisis.id}`;
    setWindows(prev => {
      if (prev.find(w => w.id === winId)) {
        focusWindow(winId);
        return prev;
      }
      return [...prev, {
        id: winId,
        type: 'info',
        title: crisis.type === 'war' ? `THEATRE RECORD: ${crisis.title.toUpperCase()}` : `SEISMIC IMPACT: ${crisis.title.toUpperCase()}`,
        x: Math.max(40, window.innerWidth / 2 - Math.min(window.innerWidth * 0.9, 500) / 2),
        y: Math.max(50, window.innerHeight / 2 - Math.min(window.innerHeight * 0.8, 420) / 2),
        maximized: window.innerWidth < 640,
        data: {
          ...crisis,
          name: crisis.title,
          capital: crisis.type === 'war' ? crisis.country : 'Seismic Center',
          region: crisis.type === 'war' ? crisis.intensity : 'Subduction Zone',
          population: 0,
          loadingInfo: crisis.type === 'war',
          wiki: crisis.type === 'war' ? null : { extract: `Incident code registered. Event magnitude ${crisis.mag} registered. Satellite feeds active on target coordinates.` }
        }
      }];
    });
    focusWindow(winId);

    if (crisis.type === 'war') {
      try {
        const wikiRes = await fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(crisis.wikiTitle)}`);
        let wikiData = null;
        if (wikiRes.ok) {
          wikiData = await wikiRes.json();
        }
        updateWindow(winId, {
          data: {
            ...crisis,
            name: crisis.title,
            capital: crisis.country,
            region: crisis.intensity,
            population: 0,
            loadingInfo: false,
            wiki: wikiData || { extract: `Conflict monitoring active. Geopolitical friction detected at coordinates.` }
          }
        });
      } catch (err) {
        updateWindow(winId, {
          data: {
            ...crisis,
            name: crisis.title,
            capital: crisis.country,
            region: crisis.intensity,
            population: 0,
            loadingInfo: false,
            error: true
          }
        });
      }
    }
  }, [focusWindow, updateWindow]);

  // Voice recording state lifted up for synchronization
  const [isVoiceRecording, setIsVoiceRecording] = useState(false);
  const [isVoiceProcessing, setIsVoiceProcessing] = useState(false);
  const [voiceErrorText, setVoiceErrorText] = useState<string | null>(null);
  const [voiceInitiator, setVoiceInitiator] = useState<'sidebar' | 'chat' | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  const startVoiceRecording = async (initiator: 'sidebar' | 'chat') => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setIsVoiceProcessing(true);
        setVoiceErrorText(null);

        try {
          const file = new File([audioBlob], 'audio.webm', { type: 'audio/webm' });
          const formData = new FormData();
          formData.append('file', file);
          formData.append('model', 'whisper-large-v3-turbo');
          formData.append('temperature', '0');
          formData.append('response_format', 'verbose_json');

          const response = await fetch('https://api.groq.com/openai/v1/audio/transcriptions', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${getGroqApiKey()}`
            },
            body: formData
          });

          if (!response.ok) {
            throw new Error(`Transcription failed: ${response.statusText}`);
          }

          const data = await response.json();
          if (data.text) {
            if (initiator === 'sidebar') {
              submitChatQuery(data.text);
            } else {
              setQueryInput(prev => prev ? `${prev} ${data.text}` : data.text);
              setTimeout(() => textareaRef.current?.focus(), 50);
            }
          }
        } catch (err: any) {
          console.error("Whisper error:", err);
          setVoiceErrorText("Transcription Error");
        } finally {
          setIsVoiceProcessing(false);
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
          }
        }
      };

      mediaRecorder.start();
      setIsVoiceRecording(true);
      setVoiceInitiator(initiator);
      setVoiceErrorText(null);

      if (initiator === 'sidebar') {
        openChatWindow();
        setChatMessages(prev => [...prev, { role: 'assistant', content: '> Listening... speak query details.' }]);
      }
    } catch (err) {
      console.error("Failed to access microphone:", err);
      setVoiceErrorText("Mic Access Denied");
    }
  };

  const stopVoiceRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsVoiceRecording(false);
    }
  }, []);

  const handleToggleVoice = useCallback((initiator: 'sidebar' | 'chat') => {
    if (isVoiceProcessing) return;
    if (isVoiceRecording) {
      stopVoiceRecording();
    } else {
      startVoiceRecording(initiator);
    }
  }, [isVoiceRecording, isVoiceProcessing, stopVoiceRecording]);

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        try {
          mediaRecorderRef.current.stop();
        } catch (e) { }
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Prevent browser gestures (pinch-to-zoom) and body scroll bounce/refresh
  useEffect(() => {
    const preventZoom = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    };

    const preventGesture = (e: Event) => {
      e.preventDefault();
    };

    document.addEventListener('touchstart', preventZoom, { passive: false });
    document.addEventListener('touchmove', preventZoom, { passive: false });
    document.addEventListener('gesturestart', preventGesture, { passive: false });
    document.addEventListener('gesturechange', preventGesture, { passive: false });

    return () => {
      document.removeEventListener('touchstart', preventZoom);
      document.removeEventListener('touchmove', preventZoom);
      document.removeEventListener('gesturestart', preventGesture);
      document.removeEventListener('gesturechange', preventGesture);
    };
  }, []);

  const handleGeolocateUser = useCallback(() => {
    if (isLocating) return;
    setIsLocating(true);

    openChatWindow();
    setChatMessages(prev => [
      ...prev,
      { role: 'assistant', content: 'SYSTEM SCAN: Locating transceiver grid...' }
    ]);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        setUserLocation({ lat, lng });
        setIsLocating(false);

        setChatMessages(prev => [
          ...prev,
          {
            role: 'assistant',
            content: `### SCAN TARGET ACQUIRED\n\nCoordinates mapped successfully:\n*   **Latitude**: \`${lat.toFixed(5)}°\`\n*   **Longitude**: \`${lng.toFixed(5)}°\`\n\n*Satellite grid aligned on transceiver node.*`
          }
        ]);
      },
      (error) => {
        console.error(error);
        setIsLocating(false);
        setChatMessages(prev => [
          ...prev,
          { role: 'assistant', content: 'SYSTEM FAIL: Geolocation matrix locked. Access denied.' }
        ]);
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }, [isLocating, openChatWindow]);

  const handleResetCamera = useCallback(() => {
    setUserLocation(null);
    setSelectedCountryName(null);
    globeRef.current?.resetView();
  }, []);

  return (
    <div className="relative w-full h-[100dvh] bg-[#1c1c1c] text-zinc-300 overflow-hidden font-mono select-none">

      {/* Scrollbar styling for markdown tables and panels */}
      <style>{`
        html, body, #root {
          overscroll-behavior: contain !important;
          overscroll-behavior-y: contain !important;
        }

        .os-window-frame {
          transition: left 0.25s cubic-bezier(0.2, 0.8, 0.2, 1),
                      top 0.25s cubic-bezier(0.2, 0.8, 0.2, 1),
                      width 0.25s cubic-bezier(0.2, 0.8, 0.2, 1),
                      height 0.25s cubic-bezier(0.2, 0.8, 0.2, 1),
                      border-radius 0.25s cubic-bezier(0.2, 0.8, 0.2, 1);
        }
        .os-window-frame.dragging-active {
          transition: none !important;
        }

        /* Firefox scrollbar tuning */
        .custom-scrollbar, 
        .markdown-table-container,
        .overflow-y-auto,
        .overflow-x-auto {
          scrollbar-width: thin !important;
          scrollbar-color: rgba(255, 255, 255, 0.15) transparent !important;
        }
        
        /* Webkit scrollbar tuning */
        .custom-scrollbar::-webkit-scrollbar,
        .markdown-table-container::-webkit-scrollbar,
        .overflow-y-auto::-webkit-scrollbar,
        .overflow-x-auto::-webkit-scrollbar {
          width: 4px !important;
          height: 1.5px !important;
        }
        .custom-scrollbar::-webkit-scrollbar-track,
        .markdown-table-container::-webkit-scrollbar-track,
        .overflow-y-auto::-webkit-scrollbar-track,
        .overflow-x-auto::-webkit-scrollbar-track {
          background: transparent !important;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb,
        .markdown-table-container::-webkit-scrollbar-thumb,
        .overflow-y-auto::-webkit-scrollbar-thumb,
        .overflow-x-auto::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.15) !important;
          border-radius: 2px !important;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover,
        .markdown-table-container::-webkit-scrollbar-thumb:hover,
        .overflow-y-auto::-webkit-scrollbar-thumb:hover,
        .overflow-x-auto::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.25) !important;
        }

        /* Hide scrollbars completely for scrollbar-none elements (tabs etc.) */
        .scrollbar-none::-webkit-scrollbar,
        .scrollbar-none::-webkit-scrollbar-thumb,
        .scrollbar-none::-webkit-scrollbar-track {
          display: none !important;
          width: 0 !important;
          height: 0 !important;
          background: transparent !important;
        }
        .scrollbar-none {
          -ms-overflow-style: none !important;
          scrollbar-width: none !important;
        }

        /* Thin scrollbar for tabs when maximized/scrolling */
        .scrollbar-thin::-webkit-scrollbar {
          height: 1px !important;
          display: block !important;
        }
        .scrollbar-thin::-webkit-scrollbar-track {
          background: transparent !important;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.18) !important;
          border-radius: 1px !important;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.35) !important;
        }
        .scrollbar-thin {
          -ms-overflow-style: auto !important;
          scrollbar-width: thin !important;
        }

        /* Shimmer keyframes for skeleton loader */
        @keyframes slow-shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
      `}</style>

      {/* 2D Flat Map Component */}
      <div className="absolute inset-0 z-0">
        <JarvisGlobe
          ref={globeRef}
          filterMode={filter}
          selectedCountryName={selectedCountryName}
          onCountrySelect={handleCountrySelect}
          onCrisisSelect={handleCrisisSelect}
          onStateSelect={handleStateSelect}
          autoRotate={false}
          showAtmosphere={true}
          userLocation={userLocation}
          showStateNames={showStateNames}
          onStatesLoaded={handleStatesLoaded}
        />
      </div>

      <div className="absolute inset-0 bg-black/10 pointer-events-none z-10" />

      {/* Sidenav (Left Panel Re-themed with blue highlight) */}
      <div className="absolute left-0 top-0 bottom-0 z-30 flex items-center pointer-events-none">
        <motion.div
          animate={{ x: leftPanelExpanded ? 0 : -56 }}
          transition={{ type: "tween", ease: "easeInOut", duration: 0.18 }}
          style={{ willChange: "transform" }}
          className="relative h-[max-content] max-h-full flex items-center pointer-events-auto"
        >
          <div className="w-14 h-[max-content] max-h-full bg-[#1c1c1c]/95 border-l-none border border-[#2d2d2d] flex flex-col items-center py-6 gap-3 shadow-xl select-none rounded-br-xl rounded-tr-xl">
            <button onClick={() => navigate('/')} className="relative group p-2 hover:bg-zinc-800 text-zinc-400 hover:text-white rounded-xl transition-all">
              <Home className="w-5 h-5" />
              <div className="absolute left-full ml-3 px-2 py-1 bg-[#121212] border border-[#2d2d2d] text-zinc-300 text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">Home</div>
            </button>

            <button onClick={openChatWindow} className="relative group p-2 hover:bg-zinc-800 text-zinc-400 hover:text-white rounded-xl transition-all">
              <Cpu className="w-5 h-5" />
              <div className="absolute left-full ml-3 px-2 py-1 bg-[#121212] border border-[#2d2d2d] text-zinc-300 text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">Chat Terminal</div>
            </button>
            <div className="text-[7px] text-zinc-500 tracking-[0.2em] uppercase font-bold">Layers</div>

            <button onClick={() => setFilter('weather')} className={cn("relative group p-2 rounded-xl transition-all", filter === 'weather' ? "bg-blue-600/15 text-blue-400 border border-blue-500/30" : "text-zinc-400 hover:bg-zinc-800")}>
              <CloudSun className="w-5 h-5" />
              <div className="absolute left-full ml-3 px-2 py-1 bg-[#121212] border border-[#2d2d2d] text-zinc-300 text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">Atmospherics</div>
            </button>
            <button onClick={() => setFilter('crises')} className={cn("relative group p-2 rounded-xl transition-all", filter === 'crises' ? "bg-blue-600/15 text-blue-400 border border-blue-500/30" : "text-zinc-400 hover:bg-zinc-800")}>
              <ShieldAlert className="w-5 h-5" />
              <div className="absolute left-full ml-3 px-2 py-1 bg-[#121212] border border-[#2d2d2d] text-zinc-300 text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">Crisis Matrix</div>
            </button>
            <button onClick={() => setFilter('population')} className={cn("relative group p-2 rounded-xl transition-all", filter === 'population' ? "bg-blue-600/15 text-blue-400 border border-blue-500/30" : "text-zinc-400 hover:bg-zinc-800")}>
              <Users className="w-5 h-5" />
              <div className="absolute left-full ml-3 px-2 py-1 bg-[#121212] border border-[#2d2d2d] text-zinc-300 text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">Population</div>
            </button>
            <button onClick={() => setFilter('flights')} className={cn("relative group p-2 rounded-xl transition-all", filter === 'flights' ? "bg-blue-600/15 text-blue-400 border border-blue-500/30" : "text-zinc-400 hover:bg-zinc-800")}>
              <Plane className="w-5 h-5" />
              <div className="absolute left-full ml-3 px-2 py-1 bg-[#121212] border border-[#2d2d2d] text-zinc-300 text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">Flights</div>
            </button>
            <button onClick={() => setFilter('network')} className={cn("relative group p-2 rounded-xl transition-all", filter === 'network' ? "bg-blue-600/15 text-blue-400 border border-blue-500/30" : "text-zinc-400 hover:bg-zinc-800")}>
              <Wifi className="w-5 h-5" />
              <div className="absolute left-full ml-3 px-2 py-1 bg-[#121212] border border-[#2d2d2d] text-zinc-300 text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">Network</div>
            </button>
            <button onClick={() => setFilter('gdp')} className={cn("relative group p-2 rounded-xl transition-all", filter === 'gdp' ? "bg-blue-600/15 text-blue-400 border border-blue-500/30" : "text-zinc-400 hover:bg-zinc-800")}>
              <Coins className="w-5 h-5" />
              <div className="absolute left-full ml-3 px-2 py-1 bg-[#121212] border border-[#2d2d2d] text-zinc-300 text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">GDP Index</div>
            </button>
            <button onClick={() => setFilter('heatmap')} className={cn("relative group p-2 rounded-xl transition-all", filter === 'heatmap' ? "bg-blue-600/15 text-blue-400 border border-blue-500/30" : "text-zinc-400 hover:bg-zinc-800")}>
              <Sun className="w-5 h-5" />
              <div className="absolute left-full ml-3 px-2 py-1 bg-[#121212] border border-[#2d2d2d] text-zinc-300 text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">Heatmap</div>
            </button>
          </div>

          <button
            onClick={() => setLeftPanelExpanded(!leftPanelExpanded)}
            className="absolute -right-5 top-1/2 -translate-y-1/2 w-5 h-10 bg-[#1c1c1c]/95 border border-l-0 border-[#2d2d2d] rounded-r-lg flex items-center justify-center cursor-pointer text-zinc-400 hover:bg-zinc-800 shadow-lg z-50"
          >
            {leftPanelExpanded ? <ChevronLeft className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
        </motion.div>
      </div>

      {/* Re-themed HUD Sidebar Controls (Right Panel Zoom In/Out/Reset consolidated here) */}
      <div className="absolute right-3 top-1/2 -translate-y-1/2 z-40 flex items-center pointer-events-auto">
        <motion.div
          animate={{ x: rightPanelExpanded ? 0 : 70 }}
          transition={{ type: "tween", ease: "easeInOut", duration: 0.18 }}
          style={{ willChange: "transform" }}
          className="relative flex items-center"
        >
          <button
            onClick={() => setRightPanelExpanded(!rightPanelExpanded)}
            className="absolute -left-5 top-1/2 -translate-y-1/2 w-5 h-10 bg-[#1c1c1c]/95 border border-r-0 border-[#2d2d2d] rounded-l-lg flex items-center justify-center cursor-pointer text-zinc-400 hover:bg-zinc-800 shadow-lg z-50"
          >
            {rightPanelExpanded ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
          </button>

          <div className="w-14 bg-[#1c1c1c]/95 border border-[#2d2d2d] py-3 rounded-2xl flex flex-col items-center gap-2 shadow-xl z-40 select-none">
            <div className="relative group flex justify-center w-full">
              <VoiceVisualizer
                compact={true}
                isRecording={isVoiceRecording}
                isProcessing={isVoiceProcessing}
                errorText={voiceErrorText}
                onToggle={() => handleToggleVoice('sidebar')}
              />
              <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 px-2 py-1 bg-[#121212] border border-[#2d2d2d] text-zinc-300 text-[10px] rounded shadow-xl opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                Voice Assistant
              </div>
            </div>

            {/* Geolocation */}
            <button
              onClick={handleGeolocateUser}
              disabled={isLocating}
              className={cn(
                "relative group p-2 hover:bg-zinc-800 rounded-xl transition-all",
                isLocating ? "text-blue-400 bg-blue-500/10 animate-pulse" : "text-zinc-400 hover:text-white"
              )}
            >
              {isLocating ? <Loader2 className="w-5 h-5 animate-spin" /> : <Locate className="w-5 h-5" />}
              <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 px-2 py-1 bg-[#121212] border border-[#2d2d2d] text-zinc-300 text-[10px] rounded shadow-xl opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                Your Location
              </div>
            </button>

            {/* Zoom In Button */}
            <button
              onClick={() => globeRef.current?.zoomIn()}
              className="relative group p-2 hover:bg-zinc-800 text-zinc-400 hover:text-white rounded-xl transition-all"
            >
              <Plus className="w-5 h-5" />
              <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 px-2 py-1 bg-[#121212] border border-[#2d2d2d] text-zinc-300 text-[10px] rounded shadow-xl opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                Zoom In
              </div>
            </button>

            {/* Zoom Out Button */}
            <button
              onClick={() => globeRef.current?.zoomOut()}
              className="relative group p-2 hover:bg-zinc-800 text-zinc-400 hover:text-white rounded-xl transition-all"
            >
              <Minus className="w-5 h-5" />
              <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 px-2 py-1 bg-[#121212] border border-[#2d2d2d] text-zinc-300 text-[10px] rounded shadow-xl opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                Zoom Out
              </div>
            </button>

            {/* Reset Camera Focus */}
            <button
              onClick={handleResetCamera}
              className="relative group p-2 hover:bg-zinc-800 text-zinc-400 hover:text-white rounded-xl transition-all"
            >
              <RefreshCw className="w-5 h-5" />
              <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 px-2 py-1 bg-[#121212] border border-[#2d2d2d] text-zinc-300 text-[10px] rounded shadow-xl opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                Reset Map Focus
              </div>
            </button>

            {/* System Settings */}
            <button
              onClick={openSettingsWindow}
              className="relative group p-2 hover:bg-zinc-800 text-zinc-400 hover:text-white rounded-xl transition-all"
            >
              <SlidersHorizontal className="w-5 h-5" />
              <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 px-2 py-1 bg-[#121212] border border-[#2d2d2d] text-zinc-300 text-[10px] rounded shadow-xl opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                System Settings
              </div>
            </button>
          </div>
        </motion.div>
      </div>

      {/* Multi-Window Layout Area */}
      <AnimatePresence>
        {windows.map((win, idx) => {
          const zIndex = win.id === activeWindowId ? 50 : 30 + idx;

          return (
            <OSWindowFrame
              key={win.id}
              win={win}
              onUpdate={updateWindow}
              onClose={handleWindowClose}
              onFocus={focusWindow}
              zIndex={zIndex}
              headerExtra={win.type === 'chat' ? (
                <div className="flex items-end gap-1.5 pointer-events-auto h-full pt-1.5">
                  {chatTabs.map(tab => {
                    const isActive = tab.id === activeTabId;
                    return (
                      <button
                        key={tab.id}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => { e.stopPropagation(); setActiveTabId(tab.id); }}
                        className={cn(
                          "flex items-center gap-2 px-3 py-1.5 text-[9px] font-mono transition-all rounded-t-lg select-none relative",
                          isActive
                            ? "bg-[#1c1c1c] text-zinc-200 border-t border-x border-[#2d2d2d] z-10 -mb-[1px] font-semibold"
                            : "bg-[#141414] text-zinc-500 hover:text-zinc-300 hover:bg-[#1a1a1a]/50 border-t border-x border-transparent"
                        )}
                      >
                        <Globe2 className={cn("w-3.5 h-3.5", isActive ? "text-blue-500" : "text-zinc-600")} />
                        <span>{tab.title}</span>
                        {chatTabs.length > 1 && (
                          <span
                            onClick={(e) => { e.stopPropagation(); handleCloseTab(tab.id); }}
                            className="hover:bg-zinc-800 hover:text-red-400 p-0.5 rounded ml-1.5 cursor-pointer font-bold font-mono text-[9px] w-3 h-3 flex items-center justify-center transition-all"
                          >
                            <X className="w-3 h-3" />
                          </span>
                        )}
                      </button>
                    );
                  })}
                  {chatTabs.length < 3 && (
                    <button
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); handleAddTab(); }}
                      className="p-1 mb-1 hover:bg-zinc-800 text-zinc-500 hover:text-white rounded-lg transition-colors flex items-center justify-center"
                      title="Add new session (Max 3)"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ) : win.type === 'info' ? (
                <div className={cn(
                  "flex items-end gap-1 overflow-y-hidden pointer-events-auto h-[calc(100%+1px)] -mb-[1px] relative z-10 pt-1.5 overflow-x-auto scrollbar-thin transition-all duration-300",
                  win.maximized
                    ? "max-w-[calc(100vw-150px)] sm:max-w-[650px]"
                    : "max-w-[calc(100vw-125px)] sm:max-w-[320px]"
                )}>
                  {win.data?.tabs?.map((tab: any) => {
                    const isActive = tab.id === win.data.activeTabId;
                    const isCountry = tab.type === 'country';

                    // Truncate state name to max 7 characters
                    const displayTitle = isCountry
                      ? ''
                      : (tab.title.length > 5 ? tab.title.slice(0, 5) + '..' : tab.title);

                    return (
                      <button
                        key={tab.id}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          updateWindow(win.id, {
                            data: {
                              ...win.data,
                              activeTabId: tab.id
                            }
                          });
                        }}
                        className={cn(
                          "flex items-center gap-1 px-1.5 py-1 text-[9px] font-mono transition-all rounded-t-lg select-none relative border-t border-x flex-shrink-0",
                          isActive
                            ? "bg-[#1c1c1c] text-zinc-200 border-[#2d2d2d] z-10 font-semibold"
                            : "bg-[#141414] text-zinc-500 hover:text-zinc-300 hover:bg-[#1a1a1a]/50 border-b border-[#2d2d2d]"
                        )}
                      >
                        {isCountry ? (
                          tab.flag ? (
                            <img src={tab.flag} alt="" className="w-4 h-3 object-cover rounded shadow-sm flex-shrink-0" />
                          ) : (
                            <span className="flex-shrink-0">🏳️</span>
                          )
                        ) : (
                          <span className="flex-shrink-0">{displayTitle}</span>
                        )}

                        {!isCountry && (
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              const remainingTabs = win.data.tabs.filter((t: any) => t.id !== tab.id);
                              let nextActiveId = win.data.activeTabId;
                              if (win.data.activeTabId === tab.id) {
                                nextActiveId = 'country';
                              }
                              updateWindow(win.id, {
                                data: {
                                  ...win.data,
                                  activeTabId: nextActiveId,
                                  tabs: remainingTabs
                                }
                              });
                            }}
                            className="hover:bg-zinc-800 hover:text-red-400 p-0.5 rounded ml-0.5 cursor-pointer font-bold font-mono text-[9px] w-3 h-3 flex items-center justify-center transition-all flex-shrink-0"
                          >
                            ×
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : undefined}
            >
              {win.type === 'chat' && (
                <div className="flex-1 flex flex-col min-h-0 relative h-full bg-[#1c1c1c]/95">
                  {/* Messages Area */}
                  <div ref={chatScrollRef} className="flex-1 py-4 px-3 overflow-y-auto text-[10px] space-y-3.5 select-none bg-[#1c1c1c]/30 min-h-0 flex flex-col">
                    {chatMessages.length === 0 ? (
                      <div className="flex items-center justify-center h-full text-zinc-500 font-mono text-xs tracking-wider animate-pulse">
                        How may I help you?
                      </div>
                    ) : (
                      <>
                        {chatMessages.map((msg, i) => (
                          <JarvisChatMessage
                            key={i}
                            msg={msg}
                            isAIResponding={isAIResponding && i === chatMessages.length - 1}
                            markdownComponents={markdownComponents}
                            setLightboxImage={setLightboxImage}
                            isLatest={i === chatMessages.length - 1}
                            textSize={textSize}
                          />
                        ))}
                        {isAIResponding && (
                          <div className="flex items-center gap-2 text-zinc-500 ml-2 text-[10px]">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            <span>{aiStatusText}</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Chat Input form - always at bottom, no layout animations */}
                  <form
                    onSubmit={(e) => { e.preventDefault(); submitChatQuery(queryInput); }}
                    className="border-t border-[#2d2d2d] px-3 py-2 bg-[#141414] flex items-end gap-3 pointer-events-auto"
                  >
                    <textarea
                      ref={textareaRef}
                      rows={1}
                      value={queryInput}
                      onChange={(e) => setQueryInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      disabled={isAIResponding}
                      placeholder="Ask J.A.R.V.I.S..."
                      className="flex-1 bg-transparent border-none outline-none text-zinc-200 placeholder:text-zinc-600 text-xs font-mono resize-none py-1 custom-scrollbar"
                      style={{ maxHeight: '120px' }}
                    />
                    <div className="flex-shrink-0 relative flex items-center justify-center w-8 h-8">
                      <AnimatePresence mode="wait">
                        {queryInput.trim() ? (
                          <motion.button
                            key="send-btn"
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            transition={{ duration: 0.15 }}
                            type="submit"
                            disabled={isAIResponding}
                            className="p-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded-md border border-blue-500/30 transition-all disabled:opacity-30 disabled:scale-100 active:scale-95 flex items-center justify-center w-8 h-8"
                          >
                            <CornerDownLeft className="w-3.5 h-3.5" />
                          </motion.button>
                        ) : (
                          <motion.div
                            key="voice-btn"
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            transition={{ duration: 0.15 }}
                            className="flex items-center justify-center w-8 h-8"
                          >
                            <VoiceVisualizer
                              compact={true}
                              size="sm"
                              isRecording={isVoiceRecording}
                              isProcessing={isVoiceProcessing}
                              errorText={voiceErrorText}
                              onToggle={() => handleToggleVoice('chat')}
                            />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </form>
                </div>
              )}

              {win.type === 'info' && (() => {
                // Find the active tab in this window's tabs
                const activeTab = win.data?.tabs?.find((t: any) => t.id === win.data.activeTabId) || win.data?.tabs?.[0];
                const tabData = activeTab ? activeTab.data : win.data;

                return (
                  <div className="flex-1 flex flex-col h-full overflow-hidden">
                    {/* Tab Content */}
                    <div className="flex-1 overflow-y-auto p-4 custom-scrollbar text-xs select-none bg-[#1c1c1c]/30">
                      {showStatesWindowId === win.id ? (
                        /* States List View */
                        <div className="space-y-3 font-mono">
                          <div className="flex justify-between items-center pb-2 border-b border-[#2d2d2d]">
                            <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider">Territorial Subdivisions</span>
                            <button
                              onClick={() => setShowStatesWindowId(null)}
                              className="p-1 rounded-full border border-blue-500/30 bg-blue-500/5 hover:bg-blue-500/15 text-blue-400 hover:text-blue-300 hover:border-blue-400/50 shadow-md shadow-blue-500/5 transition-all flex items-center justify-center cursor-pointer active:scale-95"
                            >
                              <ArrowLeft className="w-4 h-4" />
                            </button>
                          </div>

                          {(() => {
                            const states = countryStates.filter((s: any) => s.country?.toLowerCase() === tabData.name?.toLowerCase());
                            if (states.length === 0) {
                              return (
                                <div className="grid grid-cols-2 gap-1.5 relative overflow-hidden h-[220px] select-none">
                                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-zinc-800/15 to-transparent -translate-x-full animate-[slow-shimmer_2s_infinite] pointer-events-none" />
                                  {Array.from({ length: 6 }).map((_, idx) => (
                                    <div
                                      key={idx}
                                      className="h-8 bg-zinc-800/20 border border-[#2d2d2d]/30 rounded-lg animate-pulse"
                                    />
                                  ))}
                                </div>
                              );
                            }
                            return (
                              <div className="grid grid-cols-2 gap-1.5 max-h-[220px] overflow-y-auto pr-1 custom-scrollbar">
                                {states.map((st: any, idx: number) => (
                                  <button
                                    key={idx}
                                    onClick={() => {
                                      handleStateSelect(st);
                                      setShowStatesWindowId(null);
                                    }}
                                    className="text-left p-2 bg-[#121212] border border-[#2d2d2d] hover:border-blue-500/40 hover:bg-blue-500/5 hover:text-blue-400 rounded-lg text-zinc-300 transition-all text-[9px] truncate"
                                  >
                                    {st.name}
                                  </button>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                      ) : showMoreInfoWindowId === win.id ? (
                        /* More Info View */
                        <div className="space-y-3 font-mono">
                          <div className="flex justify-between items-center pb-2 border-b border-[#2d2d2d]">
                            <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider">Sovereignty Intelligence</span>
                            <button
                              onClick={() => setShowMoreInfoWindowId(null)}
                              className="p-1 rounded-full border border-blue-500/30 bg-blue-500/5 hover:bg-blue-500/15 text-blue-400 hover:text-blue-300 hover:border-blue-400/50 shadow-md shadow-blue-500/5 transition-all flex items-center justify-center cursor-pointer active:scale-95"
                            >
                              <ArrowLeft className="w-4 h-4" />
                            </button>
                          </div>

                          {tabData.loadingMoreInfo || !tabData.moreInfo ? (
                            <div className="text-[9px] text-zinc-500 py-12 text-center tracking-widest animate-pulse flex flex-col items-center justify-center gap-2">
                              <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                              <span>DECRYPTING SOVEREIGNTY SCHEMATICS...</span>
                            </div>
                          ) : (() => {
                            const info = tabData.moreInfo;
                            return (
                              <div className="space-y-3">
                                {/* Leaders Section */}
                                <div className="bg-[#121212] border border-[#2d2d2d] p-3 rounded-2xl space-y-1.5">
                                  <div className="text-[8px] text-zinc-500 uppercase tracking-widest font-bold font-mono mb-1">Government Leadership</div>
                                  <div className="grid grid-cols-2 gap-3 text-[10px] text-zinc-300">
                                    <div className="flex-col items-center gap-2">
                                      {info.presidentImage ? (
                                        <img src={info.presidentImage} alt={info.president} className="w-20 h-20 rounded-2xl object-cover border border-blue-500/30 bg-zinc-850 shadow-md shadow-blue-500/5 flex-shrink-0" />
                                      ) : (
                                        <div className="w-8 h-8 rounded-full bg-zinc-800 border border-[#2d2d2d] flex items-center justify-center text-zinc-500 font-mono text-[8px] font-bold flex-shrink-0">
                                          HS
                                        </div>
                                      )}
                                      <div className="min-w-0">
                                        <span className="text-zinc-500 block text-[7px] uppercase tracking-wider">Head of State</span>
                                        <span className="font-semibold text-zinc-200 block truncate" title={info.president}>{info.president}</span>
                                      </div>
                                    </div>
                                    <div className="flex-col items-center gap-2">
                                      {info.pmImage ? (
                                        <img src={info.pmImage} alt={info.pm} className="w-20 h-20 rounded-2xl object-cover border border-blue-500/30 bg-zinc-850 shadow-md shadow-blue-500/5 flex-shrink-0" />
                                      ) : (
                                        <div className="w-8 h-8 rounded-full bg-zinc-800 border border-[#2d2d2d] flex items-center justify-center text-zinc-500 font-mono text-[8px] font-bold flex-shrink-0">
                                          HG
                                        </div>
                                      )}
                                      <div className="min-w-0">
                                        <span className="text-zinc-500 block text-[7px] uppercase tracking-wider">{activeTab?.type === 'state' ? 'Chief Minister' : 'Head of Gov'}</span>
                                        <span className="font-semibold text-zinc-200 block truncate" title={info.pm}>{info.pm}</span>
                                      </div>
                                    </div>
                                  </div>
                                  {info.dictator && (
                                    <div className="mt-2 pt-2 border-t border-[#2d2d2d]/40 flex items-center gap-1.5 text-amber-500 text-[9px] font-semibold">
                                      <ShieldAlert className="w-3.5 h-3.5 animate-pulse" />
                                      <span>CONSOLIDATED AUTHORITY: {info.dictator.toUpperCase()}</span>
                                    </div>
                                  )}
                                </div>

                                {/* Strengths & Weaknesses */}
                                <div className="grid grid-cols-2 gap-2">
                                  <div className="bg-[#121212] border border-[#2d2d2d] p-2.5 rounded-lg">
                                    <div className="text-[8px] text-emerald-500 uppercase tracking-widest font-bold mb-1.5 font-mono">Strategic Assets</div>
                                    <ul className="space-y-1 text-[9px] text-zinc-400">
                                      {info.strengths.map((str: string, idx: number) => (
                                        <li key={idx} className="leading-tight flex items-start gap-1">
                                          <span className="text-emerald-500">✓</span>
                                          <span>{str}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>

                                  <div className="bg-[#121212] border border-[#2d2d2d] p-2.5 rounded-lg">
                                    <div className="text-[8px] text-red-500 uppercase tracking-widest font-bold mb-1.5 font-mono">Systemic Vulnerabilities</div>
                                    <ul className="space-y-1 text-[9px] text-zinc-400">
                                      {info.weaknesses.map((wk: string, idx: number) => (
                                        <li key={idx} className="leading-tight flex items-start gap-1">
                                          <span className="text-red-500">!</span>
                                          <span>{wk}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      ) : tabData?.loadingInfo ? (
                        <div className="space-y-4 relative overflow-hidden h-full select-none">
                          {/* Shimmer gradient overlay */}
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-zinc-800/15 to-transparent -translate-x-full animate-[slow-shimmer_2s_infinite] pointer-events-none" />

                          {/* Skeleton Header */}
                          <div className="flex gap-4 items-start pb-3 border-b border-[#2d2d2d]">
                            <div className="w-16 h-10 bg-zinc-800/40 rounded shadow-md" />
                            <div className="flex-1 space-y-2">
                              <div className="w-28 h-4 bg-zinc-800/45 rounded" />
                              <div className="flex gap-3">
                                <div className="w-14 h-2.5 bg-zinc-800/30 rounded" />
                                <div className="w-14 h-2.5 bg-zinc-800/30 rounded" />
                              </div>
                            </div>
                          </div>

                          {/* Skeleton Stats Grid */}
                          <div className="grid grid-cols-2 gap-2">
                            <div className="bg-[#121212]/30 border border-[#2d2d2d]/25 p-2.5 rounded-lg h-16 space-y-2">
                              <div className="w-16 h-2 bg-zinc-800/30 rounded" />
                              <div className="w-10 h-3 bg-zinc-800/40 rounded" />
                            </div>
                            <div className="bg-[#121212]/30 border border-[#2d2d2d]/25 p-2.5 rounded-lg h-16 space-y-2">
                              <div className="w-16 h-2 bg-zinc-800/30 rounded" />
                              <div className="w-10 h-3 bg-zinc-800/40 rounded" />
                            </div>
                          </div>

                          {/* Skeleton Wiki Extract */}
                          <div className="space-y-2 pt-2">
                            <div className="w-full h-3 bg-zinc-800/25 rounded" />
                            <div className="w-5/6 h-3 bg-zinc-800/25 rounded" />
                            <div className="w-4/6 h-3 bg-zinc-800/25 rounded" />
                          </div>
                        </div>
                      ) : tabData?.error ? (
                        <div className="flex flex-col items-center justify-center h-full gap-3 text-red-500">
                          <ShieldAlert className="w-6 h-6" />
                          <div className="text-[9px] tracking-widest font-mono">CHANNEL ERROR: LOCKED</div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {/* Header */}
                          <div className="flex gap-4 items-start pb-3 border-b border-[#2d2d2d]">
                            {tabData.flag && (
                              <img src={tabData.flag} alt="" className="w-16 rounded border border-zinc-800 shadow-md" />
                            )}
                            <div className="flex-1 min-w-0">
                              <h2 className="text-base font-bold text-zinc-200 font-mono tracking-wide leading-tight">{tabData.name.toUpperCase()}</h2>
                              <div className="text-zinc-400 text-[9px] flex flex-wrap gap-x-3 mt-1.5 font-mono">
                                <span className="flex items-center gap-1"><MapPin className="w-2.5 h-2.5 text-blue-500" />{tabData.capital}</span>
                                <span className="flex items-center gap-1"><Globe2 className="w-2.5 h-2.5 text-blue-500" />{tabData.region}</span>
                              </div>
                            </div>

                            {/* 3-dot dropdown menu */}
                            <div className="relative">
                              <button
                                onClick={() => setMenuOpenWindowId(menuOpenWindowId === win.id ? null : win.id)}
                                className="p-1 hover:bg-zinc-850 text-zinc-400 hover:text-zinc-200 rounded transition-colors"
                              >
                                <MoreVertical className="w-4 h-4" />
                              </button>

                              {menuOpenWindowId === win.id && (
                                <div className="absolute right-0 w-32 bg-[#141414] border border-[#2d2d2d] rounded-lg shadow-xl overflow-hidden z-50 font-mono text-[9px]">
                                  {activeTab?.type === 'country' && (
                                    <button
                                      onClick={() => {
                                        setShowStatesWindowId(win.id);
                                        setShowMoreInfoWindowId(null);
                                        setMenuOpenWindowId(null);
                                      }}
                                      className="w-full text-left px-3 py-1.5 hover:bg-zinc-800 text-zinc-300 hover:text-white flex items-center gap-1.5 transition-colors"
                                    >
                                      <MapPin className="w-3 h-3 text-blue-500" />
                                      States List
                                    </button>
                                  )}
                                  <button
                                    onClick={() => {
                                      setShowMoreInfoWindowId(win.id);
                                      setShowStatesWindowId(null);
                                      setMenuOpenWindowId(null);
                                    }}
                                    className="w-full text-left px-3 py-1.5 hover:bg-zinc-800 text-zinc-300 hover:text-white flex items-center gap-1.5 transition-colors"
                                  >
                                    <Info className="w-3 h-3 text-blue-500" />
                                    More Info
                                  </button>
                                  {(showStatesWindowId === win.id || showMoreInfoWindowId === win.id) && (
                                    <button
                                      onClick={() => {
                                        setShowStatesWindowId(null);
                                        setShowMoreInfoWindowId(null);
                                        setMenuOpenWindowId(null);
                                      }}
                                      className="w-full text-left px-3 py-1.5 border-t border-[#2d2d2d] hover:bg-zinc-800 text-zinc-400 hover:text-zinc-200 flex items-center gap-1.5 transition-colors"
                                    >
                                      <RefreshCw className="w-3 h-3 text-zinc-500" />
                                      Default View
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Stats graphs styled in tactical command center theme */}
                          {((tabData.population > 0) || (tabData.area > 0) || tabData.weather) && (
                            <div className="grid grid-cols-2 gap-2 font-mono">
                              {tabData.population > 0 && (
                                <div className="bg-[#121212] border border-[#2d2d2d] p-2.5 rounded-lg">
                                  <div className="text-[8px] text-zinc-500 tracking-wider mb-1 uppercase font-bold">Population Network</div>
                                  <div className="text-xs text-zinc-300 font-semibold">
                                    {new Intl.NumberFormat().format(tabData.population)}
                                  </div>
                                </div>
                              )}
                              {tabData.area > 0 && (
                                <div className="bg-[#121212] border border-[#2d2d2d] p-2.5 rounded-lg">
                                  <div className="text-[8px] text-zinc-500 tracking-wider mb-1 uppercase font-bold">Terrain Coverage</div>
                                  <div className="text-xs text-zinc-300 font-semibold">{new Intl.NumberFormat().format(tabData.area)} km²</div>
                                </div>
                              )}
                              {tabData.weather && (
                                <>
                                  {tabData.weather.temperature !== undefined && (
                                    <div className="bg-[#121212] border border-[#2d2d2d] p-2.5 rounded-lg">
                                      <div className="text-[8px] text-zinc-500 tracking-wider mb-1 uppercase font-bold">Atmospheric Heat</div>
                                      <div className="text-xs text-zinc-300 font-semibold">{tabData.weather.temperature}°C</div>
                                    </div>
                                  )}
                                  {tabData.weather.windspeed !== undefined && (
                                    <div className="bg-[#121212] border border-[#2d2d2d] p-2.5 rounded-lg">
                                      <div className="text-[8px] text-zinc-500 tracking-wider mb-1 uppercase font-bold">Velocity Vectors</div>
                                      <div className="text-xs text-zinc-300 font-semibold">{tabData.weather.windspeed} km/h</div>
                                    </div>
                                  )}
                                  {tabData.weather.humidity !== undefined && (
                                    <div className="bg-[#121212] border border-[#2d2d2d] p-2.5 rounded-lg">
                                      <div className="text-[8px] text-zinc-500 tracking-wider mb-1 uppercase font-bold">Moisture Saturation</div>
                                      <div className="text-xs text-zinc-300 font-semibold">{tabData.weather.humidity}%</div>
                                    </div>
                                  )}
                                  {tabData.weather.airQuality !== undefined && (
                                    <div className="bg-[#121212] border border-[#2d2d2d] p-2.5 rounded-lg">
                                      <div className="text-[8px] text-zinc-500 tracking-wider mb-1 uppercase font-bold">Air Quality Index</div>
                                      <div className="text-xs text-zinc-300 font-semibold">{tabData.weather.airQuality} (AQI: {tabData.weather.aqi})</div>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          )}

                          {/* Detailed Telemetry fields */}
                          <div className="space-y-1.5 text-[9px] text-zinc-400 font-mono">
                            {tabData.languages && tabData.languages !== 'Unknown' && (
                              <div className="flex items-center gap-2">
                                <Languages className="w-3 h-3 text-zinc-600" />
                                <span>Dialects: {tabData.languages}</span>
                              </div>
                            )}
                            {tabData.currencies && tabData.currencies !== 'Unknown' && (
                              <div className="flex items-center gap-2">
                                <Coins className="w-3 h-3 text-zinc-600" />
                                <span>Credit: {tabData.currencies}</span>
                              </div>
                            )}
                            {tabData.timezones && tabData.timezones !== 'Unknown' && (
                              <div className="flex items-center gap-2">
                                <Timer className="w-3 h-3 text-zinc-600" />
                                <span className="truncate">Synchrony zone: {tabData.timezones}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <Navigation className="w-3 h-3 text-zinc-600" />
                              <span>Coordinates: {tabData.lat?.toFixed(3)}N, {tabData.lng?.toFixed(3)}E</span>
                            </div>
                          </div>

                          {/* Landmarks Carousel */}
                          {tabData.landmarks && tabData.landmarks.length > 0 && (
                            <div className="pt-3 border-t border-[#2d2d2d]">
                              <div className="text-[8px] text-zinc-500 tracking-wider mb-1.5 font-bold uppercase flex items-center gap-1.5">
                                <Globe2 className="w-3 h-3 text-blue-500" />
                                LANDMARK RECONNAISSANCE:
                              </div>
                              <div className="flex gap-2 overflow-x-auto pb-1.5 custom-scrollbar snap-x snap-mandatory">
                                {tabData.landmarks.map((imgUrl: string, imgIdx: number) => (
                                  <div
                                    key={imgIdx}
                                    onClick={() => setLightboxImage(imgUrl)}
                                    className="relative flex-shrink-0 w-28 h-18 rounded border border-[#2d2d2d] overflow-hidden cursor-pointer hover:border-blue-500/50 transition-colors snap-start group bg-[#121212]"
                                  >
                                    <img
                                      src={imgUrl}
                                      alt={`landmark-${imgIdx}`}
                                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                      loading="lazy"
                                    />
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                      <Maximize2 className="w-4 h-4 text-white" />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Wikipedia Summary Brief */}
                          {tabData.wiki && (
                            <div className="pt-3 border-t border-[#2d2d2d]">
                              <div className="text-[8px] text-zinc-500 tracking-wider mb-1.5 font-bold uppercase">Intelligence Brief</div>
                              <p className="text-zinc-300 leading-relaxed text-[11px] font-mono">{tabData.wiki.extract}</p>
                              <div className="flex gap-4 mt-2.5">
                                {tabData.wiki.content_urls?.desktop?.page && (
                                  <a href={tabData.wiki.content_urls.desktop.page} target="_blank" rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-[9px] text-blue-400 hover:text-blue-300 transition-colors">
                                    <ExternalLink className="w-2.5 h-2.5" /> Wikipedia
                                  </a>
                                )}
                                <a href={`https://news.google.com/search?q=${encodeURIComponent(tabData.name)}`} target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-[9px] text-blue-400 hover:text-blue-300 transition-colors">
                                  <Newspaper className="w-2.5 h-2.5" /> News
                                </a>
                              </div>
                            </div>
                          )}

                          {/* Action trigger button */}
                          <div className="flex justify-center pt-1">
                            <button
                              onClick={() => {
                                openChatWindow();
                                const promptText = tabData.country
                                  ? `Synthesize target summary report for state of ${tabData.name} in ${tabData.country}. Extract cultural details, regional economic indexes, and local geography.`
                                  : `Synthesize target summary report for ${tabData.name}. Extract cultural details, economic performance indexes, and active geopolitics.`;
                                setQueryInput(promptText);
                                setTimeout(() => textareaRef.current?.focus(), 150);
                              }}
                              className="group relative flex items-center bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/30 text-[9px] font-mono tracking-[0.05em] uppercase rounded-full overflow-hidden cursor-pointer h-[3em] pl-4 pr-[3.3em] transition-all duration-300 ease-in-out w-1/2 min-w-[180px] justify-center"
                            >
                              <span className="truncate">Initialize Scan</span>
                              <div className="absolute right-[0.3em] flex items-center justify-center h-[2.2em] w-[2.2em] rounded-full bg-blue-500 text-white transition-all duration-300 ease-out group-hover:bg-blue-400 active:scale-95">
                                <svg className="w-[1.1em] h-[1.1em] text-white transition-transform duration-300 ease-out group-hover:translate-x-[0.1em] group-hover:rotate-[-25deg]" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M0 0h24v24H0z" fill="none" />
                                  <path d="M16.172 11l-5.364-5.364 1.414-1.414L20 12l-7.778 7.778-1.414-1.414L16.172 13H4v-2z" fill="currentColor" />
                                </svg>
                              </div>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
              {win.type === 'settings' && (
                <div className="flex-1 flex flex-col p-4 bg-[#1c1c1c] text-xs font-mono text-zinc-300 space-y-4">

                  {/* Text Size Control */}
                  <div className="space-y-1.5">
                    <label className="text-[9px] text-zinc-500 uppercase tracking-widest font-bold">Text Scaling</label>
                    <div className="flex gap-2">
                      {(['sm', 'md', 'lg'] as const).map((sz) => (
                        <button
                          key={sz}
                          onClick={() => setTextSize(sz)}
                          className={cn(
                            "px-3 py-1.5 border text-[9px] uppercase tracking-wider rounded-lg transition-all",
                            textSize === sz
                              ? "bg-blue-600/15 text-blue-400 border-blue-500/40 font-bold"
                              : "border-[#2d2d2d] bg-transparent text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800"
                          )}
                        >
                          {sz === 'sm' ? 'Small' : sz === 'md' ? 'Medium' : 'Large'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Map State Name Toggle */}
                  <div className="space-y-1.5 pt-2">
                    <label className="text-[9px] text-zinc-500 uppercase tracking-widest font-bold">Vector Mapping</label>
                    <div className="flex items-center justify-between p-2.5 bg-[#121212] border border-[#2d2d2d] rounded-xl">
                      <span className="text-[10px]">Show State boundaries name view</span>
                      <button
                        onClick={() => setShowStateNames(!showStateNames)}
                        className={cn(
                          "w-10 h-5 rounded-full p-0.5 transition-colors focus:outline-none flex items-center",
                          showStateNames ? "bg-blue-500 justify-end" : "bg-zinc-800 justify-start"
                        )}
                      >
                        <div className="w-4 h-4 rounded-full bg-white shadow-md animate-none" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </OSWindowFrame>
          );
        })}
      </AnimatePresence>

      {/* Close Session Confirmation Modal */}
      {tabPendingClose && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center pointer-events-auto">
          <div className="absolute inset-0 bg-black/50" onClick={() => setTabPendingClose(null)} />
          <div className="relative z-[121] w-[92%] max-w-md rounded-2xl border p-5 shadow-2xl bg-[#1c1c1c] border-[#2a2a2a] text-white">
            <h3 className="text-lg font-semibold mb-2">Close Session</h3>
            <p className="text-gray-300 mb-4 text-xs leading-relaxed">
              Are you sure you want to close this session? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="bg-transparent hover:bg-[#2a2a2a] text-gray-300 px-4 py-2 rounded-full text-xs font-medium transition-all"
                onClick={() => setTabPendingClose(null)}
              >
                Cancel
              </button>
              <button
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-full text-xs font-medium transition-all"
                onClick={() => {
                  if (tabPendingClose) {
                    executeCloseTab(tabPendingClose);
                  }
                }}
              >
                Close Session
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Modal for Image Expansion */}
      <AnimatePresence>
        {lightboxImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightboxImage(null)}
            className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 cursor-zoom-out pointer-events-auto"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="relative max-w-4xl max-h-[85vh] overflow-hidden rounded-lg border border-zinc-800 bg-[#121212] shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={lightboxImage}
                alt="expanded-recon"
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />
              <button
                onClick={() => setLightboxImage(null)}
                className="absolute top-3 right-3 p-1.5 bg-black/60 hover:bg-zinc-800 text-zinc-400 hover:text-white rounded-md border border-zinc-800 transition-all"
              >
                <X className="w-4 h-4" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Horizontal Taskbar Dock */}
      <AnimatePresence>
        {showTaskbar && (
          <motion.div
            initial={{ y: 60, opacity: 0, x: "-50%" }}
            animate={{ y: 0, opacity: 1, x: "-50%" }}
            exit={{ y: 60, opacity: 0, x: "-50%" }}
            transition={{
              type: "spring",
              stiffness: 260,
              damping: 24,
            }}
            className=" fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center justify-center gap-4 px-3 py-2 h-[58px] min-w-[220px] max-w-full rounded-[22px] border border-white/[0.08] bg-gradient-to-b from-[#1d1d1d]/95 to-[#111111]/95 backdrop-blur-2xl shadow-[0_8px_40px_rgba(0,0,0,0.45)] pointer-events-auto "
          >
            {/* JARVIS */}
            {(() => {
              const chatWin = windows.find((w) => w.type === "chat");
              const isActive = chatWin && activeWindowId === chatWin.id;

              if (!chatWin) return null;

              return (
                <button
                  onClick={() => focusWindow(chatWin.id)}
                  className="group/task relative flex flex-col items-center"
                >
                  <div
                    className=" flex items-center justify-center w-10 h-10 rounded-2xl border transition-all duration-200 bg-blue-500/10 border-blue-500/25 text-blue-400 shadow-[0_0_20px_rgba(59,130,246,0.08)] group-hover/task:-translate-y-1 group-hover/task:scale-110 active:scale-95 "
                  >
                    <Cpu className="w-5 h-5" />
                  </div>

                  <div
                    className=" absolute bottom-full mb-3 px-2.5 py-1 rounded-lg border border-zinc-700/50 bg-[#111111] text-[10px] text-zinc-300 font-medium tracking-wide whitespace-nowrap opacity-0 translate-y-1 transition-all duration-200 pointer-events-none group-hover/task:opacity-100 group-hover/task:translate-y-0 "
                  >
                    JARVIS
                  </div>

                  <div className="absolute -bottom-1.5 flex justify-center w-full">
                    <div
                      className={cn(
                        "rounded-full transition-all duration-300",
                        isActive
                          ? "w-4 h-1 bg-blue-500"
                          : "w-1.5 h-1 bg-zinc-600"
                      )}
                    />
                  </div>
                </button>
              );
            })()}

            {/* COUNTRY WINDOWS */}
            {windows
              .filter((w) => w.type === "info")
              .map((win) => {
                const isActive = activeWindowId === win.id;

                const flag =
                  win.data?.flag ||
                  win.data?.tabs?.find((t: any) => t.type === "country")?.flag;

                const title = win.title || "Country";

                return (
                  <button
                    key={win.id}
                    onClick={() => focusWindow(win.id)}
                    className="group/task relative flex flex-col items-center"
                  >
                    <div
                      className=" transition-all duration-200 group-hover/task:-translate-y-1 group-hover/task:scale-110 active:scale-95 "
                    >
                      {flag ? (
                        <img
                          src={flag}
                          alt={title}
                          className=" w-10 h-10 object-cover rounded-2xl border border-zinc-700/60 shadow-lg "
                        />
                      ) : (
                        <div
                          className=" w-10 h-10 rounded-2xl bg-amber-500/10 border border-amber-500/25 flex items-center justify-center text-amber-400 "
                        >
                          <ShieldAlert className="w-5 h-5" />
                        </div>
                      )}
                    </div>

                    <div
                      className=" absolute bottom-full mb-3 px-2.5 py-1 rounded-lg border border-zinc-700/50 bg-[#111111] text-[10px] 
                       text-zinc-300 font-medium whitespace-nowrap opacity-0 translate-y-1 transition-all duration-200 pointer-events-none group-hover/task:opacity-100 group-hover/task:translate-y-0
                "
                    >
                      {title}
                    </div>

                    <div className="absolute -bottom-1.5 flex justify-center w-full">
                      <div
                        className={cn(
                          "rounded-full transition-all duration-300",
                          isActive
                            ? "w-4 h-1 bg-blue-500"
                            : "w-1.5 h-1 bg-zinc-600"
                        )}
                      />
                    </div>
                  </button>
                );
              })}

            {/* SETTINGS */}
            {(() => {
              const settingsWin = windows.find((w) => w.type === "settings");
              const isActive =
                settingsWin && activeWindowId === settingsWin.id;

              if (!settingsWin) return null;

              return (
                <button
                  onClick={() => focusWindow(settingsWin.id)}
                  className="group/task relative flex flex-col items-center"
                >
                  <div
                    className=" flex items-center justify-center w-10 h-10 rounded-2xl border border-zinc-700/60 bg-zinc-800/70 
                     text-zinc-300 transition-all duration-200 group-hover/task:-translate-y-1 group-hover/task:scale-110 active:scale-95
              "
                  >
                    <Settings className="w-5 h-5" />
                  </div>

                  <div
                    className="absolute bottom-full mb-3 px-2.5 py-1 rounded-lg border border-zinc-700/50 bg-[#111111] text-[10px] text-zinc-300 font-medium 
                    whitespace-nowrap opacity-0 translate-y-1 transition-all duration-200 pointer-events-none group-hover/task:opacity-100 group-hover/task:translate-y-0
              "
                  >
                    System Settings
                  </div>

                  <div className="absolute -bottom-1.5 flex justify-center w-full">
                    <div
                      className={cn(
                        "rounded-full transition-all duration-300",
                        isActive
                          ? "w-4 h-1 bg-blue-500"
                          : "w-1.5 h-1 bg-zinc-600"
                      )}
                    />
                  </div>
                </button>
              );
            })()}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom Status Bar HUD (Re-themed to Slate Gray #1c1c1c) */}
      <div className="absolute bottom-0 inset-x-0 h-6 bg-[#121212] border-t border-[#2d2d2d]/30 z-20 flex items-center justify-between px-4 pointer-events-none">
        <div className="flex items-center gap-2 pointer-events-auto">
          <button
            onClick={() => setShowTaskbar(!showTaskbar)}
            className="p-0.5 hover:bg-zinc-800 text-zinc-400 hover:text-white rounded transition-colors"
          >
            {showTaskbar ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
          </button>
          <div className="flex items-center gap-4 text-[8px] text-zinc-500 tracking-widest font-mono select-none">
            <span>{windows.length} WINDOW{windows.length !== 1 ? 'S' : ''} ACTIVE</span>
          </div>
        </div>
        <div className="flex items-center gap-4 text-[8px] text-zinc-500 tracking-wider font-mono">
          <span>ACTIVE GRID: {filter.toUpperCase()}</span>
          <SystemClock />
        </div>
      </div>

    </div>
  );
};

export default OG;
